import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * HomeScreen — the Putty viewer chrome wired to the REAL Putty collection API.
 *
 * This is the root screen of the standalone alpha app (alpha.putty.me). It
 * renders live data through the viewer's visual system. The mapping from the
 * brief:
 *
 *   - each GROUP      (collection `layerIndex` 0)        → a PROJECT  (sidebar section head)
 *   - each COLLECTION (`layerIndex` 1, parented to a group) → a CHANNEL (sidebar child)
 *   - a collection's BLOCKS                              → the grid of tiles
 *
 * Collections with no group parent (standalone / orphaned) are gathered under a
 * synthetic "Ungrouped" project so nothing is silently hidden. The user's
 * Primary group surfaces as "Personal".
 *
 * Sidebar order is reverse-chronological by most-recent activity (a
 * collection's `lastAddedAt`, a group's newest child). A quick-filter box
 * narrows the tree by title; clicking a tile opens the block in a modal;
 * images render at their natural aspect ratio in a masonry grid.
 *
 * Reuses `viewer-chrome.scss` for the chrome plus `home-screen.scss` for the
 * few data-specific tweaks. The collection endpoints are user-scoped, so this
 * needs an authenticated session; auth/empty/error states render inline.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildCollectionCanvas, chatWithCollection, fetchBlockEnvelope, getCollectionDetails, listBlocks, listCollectionChildren, listCollections, listCollectionMembers, listGroups, listMentions, listUserAnnotations, markMentionRead, } from "@/api/query-api";
import { createChildCollection, createCommentAnnotation, deleteUserAnnotation, } from "@/api/command-api";
import { useAccountMe } from "@/hooks/use-account-me";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDuration, useAllAnnotations, useBlockAnnotations, useBlockAnnotationsRead, useShowAnnotations, } from "./annotations";
import { BlockBody, blockCaption, blockLinkUrl, blockMediaUrl, blockSnippet, blockTimestamp, formatClockTime, formatDate, hashHue, postitTint, relativeTime, relativeTimeMs, } from "./block-visuals";
import { ensureImageSeqs, formatImageSeq, renameImage, useImageLabelStore } from "./image-labels";
import { QueryCanvasOverlay } from "./query-canvas-overlay";
import { createCanvas } from "./canvas-model";
import { QuickCapture } from "./quick-capture";
import { SearchModal, } from "./search-modal";
import { captureFullPageShot, scrapeScreenshots, useBlockShot, useFullPageShot, useShotCount, useShowShotsByDefault, } from "./urlbox-shots";
import "./viewer-chrome.scss";
import "./home-screen.scss";
// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------
/** Synthetic group id for collections that have no group parent. */
const UNGROUPED_ID = "__ungrouped__";
/** Stale window for the (rarely-changing) group/collection lists. */
const LIST_STALE_MS = 60_000;
/**
 * Max blocks handed to the collection-chat endpoint when asking a question to
 * build a canvas. Mirrors the server's `MAX_COLLECTION_CHAT_BLOCK_IDS` cap so a
 * large collection degrades to "ask over the first N" rather than 400-ing.
 */
const MAX_CANVAS_ASK_BLOCKS = 50;
function groupTitle(g) {
    if (g.title && g.title.trim())
        return g.title;
    if (g.type === "primary")
        return "Personal";
    if (g.id === UNGROUPED_ID)
        return "Ungrouped";
    return "Untitled group";
}
function collectionTitle(c) {
    return c.title && c.title.trim() ? c.title : "Untitled collection";
}
function swatchColor(g) {
    return g.metadata.color ?? `hsl(${hashHue(g.id)} 45% 62%)`;
}
/** Recency for ordering: most-recently-added block, else creation time. */
function recencyMs(c) {
    const t = c.lastAddedAt ?? c.createdAt;
    const ms = t ? Date.parse(t) : 0;
    return Number.isNaN(ms) ? 0 : ms;
}
// Time formatting helpers (formatDate / relativeTime / blockTimestamp / …)
// now live in ./block-visuals so the search results grid shares them.
/**
 * Case-insensitive substring match across a block's human-readable text, used
 * by the in-collection search. `q` is expected pre-trimmed and lowercased.
 */
function blockMatchesQuery(block, q) {
    if (!q)
        return true;
    const haystack = [block.title, block.summary, block.context, blockSnippet(block), block.type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(q);
}
function entryRecencyMs(entry) {
    let max = recencyMs(entry.group);
    for (const c of entry.channels)
        max = Math.max(max, recencyMs(c));
    return max;
}
// ---------------------------------------------------------------------------
// URL persistence for the current view (path-based)
// ---------------------------------------------------------------------------
//
// The selected view lives in the PATH so the browser Back/Forward buttons walk
// between views and every URL is a clean, shareable deep link:
//
//   /<collection>            a collection grid (`<collection>` = pretty slug, else raw `col_…` id)
//   /<collection>/<blk_…>    a collection with one block's modal open
//   /g/<col_…>               a group (project) overview
//   /activity                the activity feed
//   /                        nothing selected (falls back to the most-recent group)
//
// User navigations pushState a new history entry (see `pushView`); the mirror
// effect only ever replaceState's the canonical URL for the *current* view, so
// programmatic settles (slug→id, default group) and Back/Forward never spam the
// history. Groups and collections share the `col_…` id space, so groups take a
// `/g/` prefix to stay unambiguous; `g`, `activity`, and `dev` (the dev surface,
// see `index.tsx`) are therefore reserved and never emitted as a collection slug.
//
// Sidebar rows, cards, and tiles render as real `<a href>` links (not scripted
// buttons): that is what gives middle-click / ⌘-click their native "open in new
// tab" behavior with zero JS. See `collectionHref`/`groupHref`/`activityHref`
// and `isPlainClick` below.
//
// A collection segment is EITHER a raw id (`col_…`) or the collection's pretty
// `slug`. We only ever *write* a slug we can map back to its id from the loaded
// list (see `idToSlug`), so every emitted URL round-trips; everything else falls
// back to the raw id.
const GROUP_PATH = "g";
const ACTIVITY_PATH = "activity";
/** First-segment words that can't double as a collection slug (see `idToSlug`). */
const RESERVED_FIRST_SEGMENTS = new Set([GROUP_PATH, ACTIVITY_PATH, "dev"]);
/** True when a path segment is a raw collection id rather than a pretty slug. */
function looksLikeCollectionId(value) {
    return value.startsWith("col_");
}
/** Decoded, empties-stripped path segments of the current location. */
function pathSegments() {
    if (typeof window === "undefined")
        return [];
    return window.location.pathname
        .split("/")
        .filter(Boolean)
        .map((s) => {
        try {
            return decodeURIComponent(s);
        }
        catch {
            return s;
        }
    });
}
function readUrlSelection() {
    const [first, second] = pathSegments();
    if (!first)
        return undefined;
    if (first === ACTIVITY_PATH)
        return { kind: "activity" };
    if (first === GROUP_PATH)
        return second ? { kind: "group", id: second } : undefined;
    return looksLikeCollectionId(first)
        ? { kind: "collection", id: first }
        : { kind: "collection-slug", slug: first };
}
/** The open block id parsed from a `/<collection>/<blk_…>` path, if any. */
function readBlockParam() {
    const [first, second] = pathSegments();
    if (!second)
        return undefined;
    // `/g/<id>` and `/activity` never carry a block in their second segment.
    if (first === GROUP_PATH || first === ACTIVITY_PATH)
        return undefined;
    return second;
}
/**
 * The canonical path for a view. `collectionValue` is the pretty slug when one
 * is known and round-trippable, else the raw id; `blockId` appends the open
 * block. A nullish selection maps to root.
 */
function selectionPath(selection, collectionValue, blockId) {
    if (selection?.kind === "group")
        return `/${GROUP_PATH}/${encodeURIComponent(selection.id)}`;
    if (selection?.kind === "activity")
        return `/${ACTIVITY_PATH}`;
    if (selection?.kind === "collection") {
        const base = `/${encodeURIComponent(collectionValue ?? selection.id)}`;
        return blockId ? `${base}/${encodeURIComponent(blockId)}` : base;
    }
    return "/";
}
/**
 * Replace the URL with the canonical path for the current view. Never pushes —
 * history entries are created only by explicit user navigation (`pushView`),
 * so this is safe to run on every state settle without spamming Back.
 */
function writeSelectionPath(selection, collectionValue, blockId) {
    if (typeof window === "undefined")
        return;
    const path = selectionPath(selection, collectionValue, blockId);
    if (path === window.location.pathname)
        return;
    window.history.replaceState(window.history.state, "", path);
}
// `<a href>` builders for middle-click / ⌘-click. Collection links use the raw
// id (not the pretty slug) for simplicity — the opened tab rewrites the URL to
// the slug once its collection list loads (see the URL-write effect).
function collectionHref(idOrSlug, blockId) {
    return selectionPath({ kind: "collection", id: idOrSlug }, undefined, blockId);
}
function groupHref(id) {
    return selectionPath({ kind: "group", id });
}
function activityHref() {
    return selectionPath({ kind: "activity" });
}
/**
 * True for a plain primary click the SPA should handle in place. Modified or
 * non-primary clicks (⌘/Ctrl/Shift/Alt, middle button) return false so they
 * fall through to the anchor's native open-in-new-tab/window behavior.
 */
function isPlainClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}
// ---------------------------------------------------------------------------
// Pinned collections (client-side, prototype)
// ---------------------------------------------------------------------------
//
// Like annotations, pins are a design prototype: a set of collection ids kept
// in localStorage so favourites survive reloads but never leave the device.
// The sidebar surfaces them in a dedicated "Pinned" section above the groups.
const PINS_KEY = "vlab:pinned-collections";
function loadPinnedIds() {
    try {
        const raw = localStorage.getItem(PINS_KEY);
        if (!raw)
            return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return new Set();
        return new Set(parsed.filter((x) => typeof x === "string"));
    }
    catch {
        return new Set();
    }
}
function savePinnedIds(ids) {
    try {
        localStorage.setItem(PINS_KEY, JSON.stringify([...ids]));
    }
    catch {
        // storage disabled / quota exceeded — prototype, so silently drop.
    }
}
// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const GhostMark = () => (_jsx("img", { className: "vlab-ghost", src: "/favicon.png", alt: "Putty", width: 22, height: 22, draggable: false }));
/** FontAwesome solid star, inlined so the alpha app needs no icon dependency. */
const StarIcon = ({ className }) => (_jsx("svg", { className: className, viewBox: "0 0 576 512", width: "1em", height: "1em", fill: "currentColor", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z" }) }));
/** FontAwesome solid magnifying glass, inlined for the sidebar search button. */
const SearchIcon = ({ className }) => (_jsx("svg", { className: className, viewBox: "0 0 512 512", width: "1em", height: "1em", fill: "currentColor", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" }) }));
/** FontAwesome solid bell, inlined for the Latest Activity tab. */
const BellIcon = ({ className }) => (_jsx("svg", { className: className, viewBox: "0 0 448 512", width: "1em", height: "1em", fill: "currentColor", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M224 0c-17.7 0-32 14.3-32 32V51.2C119 66 64 130.6 64 208v18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3c-8.4 9.4-10.4 22.9-5.3 34.4S19.4 416 32 416H416c12.6 0 24-7.4 29.2-18.9s3.1-25-5.3-34.4l-7.4-8.3C401.3 319.2 384 273.9 384 226.8V208c0-77.4-55-142-128-156.8V32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3H224 160c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7z" }) }));
const Empty = ({ children }) => (_jsx("div", { className: "vlab-panel", children: _jsx("div", { className: "vlab-empty", children: children }) }));
// The tile cell letterboxes the image (object-fit: contain), but region
// coordinates are normalized to the image's own box — as captured in the modal,
// whose wrapper hugs the image. So the hover overlay must cover the *painted*
// box within the square cell, not the cell itself. Derive it from the aspect
// ratio: a wide image pillarboxes vertically, a tall one letterboxes
// horizontally.
function containBoxStyle(ratio) {
    if (ratio >= 1) {
        const h = 1 / ratio;
        return { left: 0, right: 0, top: `${((1 - h) / 2) * 100}%`, height: `${h * 100}%` };
    }
    return { top: 0, bottom: 0, left: `${((1 - ratio) / 2) * 100}%`, width: `${ratio * 100}%` };
}
// --- Region hover card (shared by the grid tile + the modal) ----------------
/** The image data URLs to preview, preferring the multi-image quick-box array. */
function annotationPreviewImages(a) {
    return a.imageDataUrls?.length ? a.imageDataUrls : a.imageDataUrl ? [a.imageDataUrl] : [];
}
/** Whether an annotation has anything worth floating in a hover preview. */
function annotationHasPreview(a) {
    return (a.text?.trim().length ?? 0) > 0 || annotationPreviewImages(a).length > 0;
}
/**
 * Percentage placement for a tile's hover card relative to the contained-image
 * overlay box: flips above/below by the region's vertical centre and anchors to
 * whichever horizontal edge keeps the card on-tile. No measuring needed — the
 * card lives in a non-clipped layer, so it can spill past the square cell.
 */
function tileHoverCardStyle(region) {
    const below = region.y + region.h / 2 < 0.5;
    const anchorRight = region.x + region.w / 2 > 0.5;
    const style = {};
    if (below)
        style.top = `calc(${(region.y + region.h) * 100}% + 6px)`;
    else
        style.bottom = `calc(${(1 - region.y) * 100}% + 6px)`;
    if (anchorRight)
        style.right = `${Math.max(0, (1 - (region.x + region.w)) * 100)}%`;
    else
        style.left = `${Math.max(0, region.x * 100)}%`;
    return { style, placement: below ? "below" : "above" };
}
/**
 * Read-only preview of a region annotation, floated above/below its box while
 * the box (or its rail card) is hovered. Non-interactive by design so it never
 * steals the hover that summons it — the live, editable copy lives in the rail.
 */
const AnnotationHoverCard = ({ text, images, placement, style, variant, refNumber, authorName }) => {
    const hasHead = refNumber !== undefined || authorName !== undefined;
    return (_jsxs("div", { className: `vld-anno-hovercard vld-anno-hovercard--${variant} vld-anno-hovercard--${placement}`, style: style, "aria-hidden": "true", children: [hasHead ? (_jsxs("div", { className: "vld-anno-hovercard__head", children: [refNumber !== undefined ? (_jsxs("span", { className: "vld-anno-hovercard__pin", children: ["#", refNumber] })) : undefined, authorName !== undefined ? (_jsx("span", { className: "vld-anno-hovercard__author", children: authorName })) : undefined] })) : undefined, text ? _jsx("div", { className: "vld-anno-hovercard__text", children: text }) : undefined, images.map((src, i) => (_jsx("img", { className: "vld-anno-hovercard__img", src: src, alt: "" }, i)))] }));
};
/**
 * Image/video media for a tile, with the block's region annotations overlaid as
 * squares that fade in on hover. Mirrors the modal's `.vld-anno-region` look so
 * the preview reads as the same marks the user drew.
 */
const TileMedia = ({ src, alt, regions, ratio, onRatio, hoveredRegionId, onRegionHover }) => {
    return (_jsxs("div", { className: "vld-media", children: [_jsx("img", { src: src, alt: alt, loading: "lazy", draggable: false, onLoad: (e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth > 0 && img.naturalHeight > 0)
                        onRatio(img.naturalWidth / img.naturalHeight);
                } }), regions.length > 0 && ratio !== undefined ? (_jsx("div", { className: "vld-tile-regions", style: containBoxStyle(ratio), children: regions.map((r) => (_jsx("span", { className: `vld-tile-region${hoveredRegionId === r.id ? " is-hover" : ""}`, style: rectStyle(r.region), onMouseEnter: () => onRegionHover(r.id), onMouseLeave: () => onRegionHover(undefined) }, r.id))) })) : undefined] }));
};
// --- Image labels (local-only prototype) -----------------------------------
// Image blocks have no real display name, so the caption defaults to "Image".
// Product wants them to read as incrementing, zero-padded numbers ("004") that
// can be renamed inline. The number + any rename live in localStorage only
// (see `./image-labels` and the README's "Temporary local-prototyping hacks").
/**
 * Read-only display label for a block's caption: for image blocks, the manual
 * rename, else the zero-padded auto number (falling back to `blockCaption`
 * until a sequence has been assigned). Everything else keeps `blockCaption`.
 */
function useBlockDisplayLabel(block) {
    const labels = useImageLabelStore();
    if (block.type !== "image")
        return blockCaption(block);
    const entry = labels[block.id];
    if (!entry)
        return blockCaption(block);
    return entry.name ?? formatImageSeq(entry.seq);
}
/**
 * Inline-editable caption for an image tile: shows the local number (or manual
 * rename) and turns into a text field on double-click. Subscribes to the label
 * store so a rename anywhere reflects here. Only mounted for image blocks, so
 * non-image tiles don't re-render on label changes.
 */
const EditableImageName = ({ block }) => {
    const labels = useImageLabelStore();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const inputRef = useRef(null);
    useEffect(() => {
        if (editing)
            inputRef.current?.select();
    }, [editing]);
    const entry = labels[block.id];
    // Before a sequence is assigned (first paint), fall back to the caption.
    const display = entry ? entry.name ?? formatImageSeq(entry.seq) : blockCaption(block);
    const beginEdit = () => {
        setDraft(entry?.name ?? (entry ? formatImageSeq(entry.seq) : ""));
        setEditing(true);
    };
    const commit = () => {
        renameImage(block.id, draft);
        setEditing(false);
    };
    if (editing) {
        return (_jsx("input", { ref: inputRef, className: "vlab-tile__caption-input", value: draft, autoFocus: true, "aria-label": "Rename image", onChange: (e) => setDraft(e.target.value), onKeyDown: (e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    commit();
                }
                else if (e.key === "Escape") {
                    e.preventDefault();
                    setEditing(false);
                }
            }, onBlur: commit, 
            // The caption sits outside the tile's link, but keep clicks from
            // bubbling to any future tile-level handlers (drag, open, …).
            onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation() }));
    }
    return (_jsx("span", { className: "vlab-tile__caption-name vlab-tile__caption-name--editable", onDoubleClick: beginEdit, title: "Double-click to rename", children: display }));
};
/**
 * Caption name for a tile. Image blocks render the inline-editable local label;
 * every other block type renders its plain caption.
 */
const BlockCaptionName = ({ block }) => block.type === "image" ? (_jsx(EditableImageName, { block: block })) : (_jsx("span", { className: "vlab-tile__caption-name", children: blockCaption(block) }));
const DataBlockTile = React.memo(({ block, href, hidden = false, onOpen }) => {
    const annotations = useBlockAnnotationsRead(block.id);
    const [showAnnotations] = useShowAnnotations();
    const regions = useMemo(() => showAnnotations
        ? annotations
            .filter((a) => a.region)
            .map((a) => ({
            id: a.id,
            region: a.region,
            text: a.text,
            images: annotationPreviewImages(a),
        }))
        : [], [annotations, showAnnotations]);
    const media = blockMediaUrl(block);
    const linkUrl = media === undefined ? blockLinkUrl(block) : undefined;
    const count = annotations.length;
    const countLabel = `${count} ${count === 1 ? "comment" : "comments"}`;
    // Region hover preview: track the natural ratio (to place the non-clipped
    // overlay over the contained image) and which region square is hovered, then
    // float its comment above/below — mirroring the modal.
    const [ratio, setRatio] = useState(undefined);
    const [hoveredRegionId, setHoveredRegionId] = useState(undefined);
    const hoveredRegion = useMemo(() => (hoveredRegionId ? regions.find((r) => r.id === hoveredRegionId) : undefined), [regions, hoveredRegionId]);
    const hoverCard = useMemo(() => {
        if (!hoveredRegion)
            return undefined;
        const hasPreview = (hoveredRegion.text?.trim().length ?? 0) > 0 || hoveredRegion.images.length > 0;
        return hasPreview ? tileHoverCardStyle(hoveredRegion.region) : undefined;
    }, [hoveredRegion]);
    // Debug screenshots (urlbox): once a block has been scraped, swap its card
    // for the live website screenshot — on hover, or always when the
    // "show by default" toggle is on (see the debug dock).
    const shot = useBlockShot(block.id);
    const [showShotsByDefault] = useShowShotsByDefault();
    const [hovered, setHovered] = useState(false);
    // Screenshots are a pure-link affordance. A block that carries its own media
    // (image/video) must never swap to the urlbox shot — that's what flashed a
    // black-letterboxed render in on hover. Guards against stale shots cached for
    // image blocks before the scraper learned to skip them.
    const showShot = shot !== undefined && media === undefined && (showShotsByDefault || hovered);
    return (_jsxs("div", { className: "vlab-tile", "data-vld-block-id": block.id, style: hidden ? { display: "none" } : undefined, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { className: "vld-tile-frame", children: [_jsx("a", { href: href, className: "vlab-tile__frame vld-hit", onClick: (e) => {
                            if (!isPlainClick(e))
                                return;
                            e.preventDefault();
                            onOpen(block.id);
                        }, children: showShot ? (_jsx("div", { className: "vld-shot", children: _jsx("img", { src: shot, alt: block.title ?? "Screenshot", loading: "lazy", draggable: false }) })) : media ? (_jsx(TileMedia, { src: media, alt: block.title ?? "", regions: regions, ratio: ratio, onRatio: setRatio, hoveredRegionId: hoveredRegionId, onRegionHover: setHoveredRegionId })) : (_jsx(BlockBody, { block: block })) }), linkUrl ? (_jsx("a", { className: "vld-open", href: linkUrl, target: "_blank", rel: "noreferrer", onClick: (e) => e.stopPropagation(), title: "Open link", "aria-label": "Open link", children: "\u2192" })) : undefined, showAnnotations && count > 0 ? (_jsx("span", { className: "vld-comment-badge", title: countLabel, "aria-label": countLabel, children: count })) : undefined, hoverCard && hoveredRegion && ratio !== undefined && showShot === false ? (_jsx("div", { className: "vld-tile-anno", style: containBoxStyle(ratio), "aria-hidden": "true", children: _jsx(AnnotationHoverCard, { text: hoveredRegion.text, images: hoveredRegion.images, placement: hoverCard.placement, style: hoverCard.style, variant: "tile" }) })) : undefined] }), _jsxs("div", { className: "vlab-tile__caption", children: [_jsx(BlockCaptionName, { block: block }), _jsx("span", { className: "vlab-tile__caption-by", title: formatDate(blockTimestamp(block)), children: relativeTime(blockTimestamp(block)) })] })] }));
});
DataBlockTile.displayName = "DataBlockTile";
const KIND_META = [
    { kind: "text", glyph: "💬", label: "Note" },
    { kind: "image", glyph: "🖼", label: "Image" },
    { kind: "voice", glyph: "🎙", label: "Voice" },
    { kind: "link", glyph: "🔗", label: "Link" },
];
function rectStyle(r) {
    return {
        left: `${r.x * 100}%`,
        top: `${r.y * 100}%`,
        width: `${r.w * 100}%`,
        height: `${r.h * 100}%`,
    };
}
/** Render text with the first occurrence of each quote wrapped in a marker. */
function buildTextSegments(text, quoteAnnos, refNumbers) {
    if (!text || quoteAnnos.length === 0)
        return text;
    const hits = [];
    for (const a of quoteAnnos) {
        if (!a.quote)
            continue;
        const start = text.indexOf(a.quote);
        if (start < 0)
            continue;
        const end = start + a.quote.length;
        if (hits.some((h) => start < h.end && end > h.start))
            continue; // skip overlaps
        hits.push({ start, end, id: a.id });
    }
    if (hits.length === 0)
        return text;
    hits.sort((x, y) => x.start - y.start);
    const nodes = [];
    let cursor = 0;
    for (const h of hits) {
        if (h.start > cursor)
            nodes.push(text.slice(cursor, h.start));
        nodes.push(_jsxs("mark", { className: "vld-anno-mark", children: [text.slice(h.start, h.end), _jsx("sup", { className: "vld-anno-mark__n", children: refNumbers.get(h.id) })] }, h.id));
        cursor = h.end;
    }
    if (cursor < text.length)
        nodes.push(text.slice(cursor));
    return nodes;
}
// --- Composer (one form, four kinds) ---------------------------------------
const AnnotationComposer = ({ kind, candidates, onCancel, onSave }) => {
    const [text, setText] = useState("");
    const [imageDataUrl, setImageDataUrl] = useState();
    const [linkBlockId, setLinkBlockId] = useState(candidates[0]?.id ?? "");
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [audioDataUrl, setAudioDataUrl] = useState();
    const [audioDurationMs, setAudioDurationMs] = useState(0);
    const recRef = useRef(undefined);
    const chunksRef = useRef([]);
    const startRef = useRef(0);
    useEffect(() => {
        if (!recording)
            return;
        const t = window.setInterval(() => setElapsed(Date.now() - startRef.current), 200);
        return () => window.clearInterval(t);
    }, [recording]);
    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = () => setImageDataUrl(reader.result);
        reader.readAsDataURL(file);
    };
    const startRec = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data.size > 0)
                    chunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                const reader = new FileReader();
                reader.onload = () => setAudioDataUrl(reader.result);
                reader.readAsDataURL(blob);
                setAudioDurationMs(Date.now() - startRef.current);
                for (const tr of stream.getTracks())
                    tr.stop();
            };
            startRef.current = Date.now();
            mr.start();
            recRef.current = mr;
            setRecording(true);
        }
        catch {
            setText("Microphone unavailable — check permissions.");
        }
    };
    const stopRec = () => {
        recRef.current?.stop();
        setRecording(false);
    };
    const canSave = kind === "text"
        ? text.trim().length > 0
        : kind === "image"
            ? imageDataUrl !== undefined
            : kind === "voice"
                ? audioDataUrl !== undefined
                : linkBlockId !== "";
    const save = () => {
        if (!canSave)
            return;
        const a = { kind, text: text.trim() || undefined };
        if (kind === "image")
            a.imageDataUrl = imageDataUrl;
        if (kind === "voice") {
            a.audioDataUrl = audioDataUrl;
            a.audioDurationMs = audioDurationMs;
        }
        if (kind === "link") {
            a.linkBlockId = linkBlockId;
            a.linkLabel = candidates.find((c) => c.id === linkBlockId)?.label;
        }
        onSave(a);
    };
    return (_jsxs("div", { className: "vld-anno-composer", children: [kind === "image" ? (_jsxs(_Fragment, { children: [_jsx("input", { type: "file", accept: "image/*", onChange: onPickImage }), imageDataUrl ? (_jsx("img", { className: "vld-anno-composer__preview", src: imageDataUrl, alt: "attachment preview" })) : undefined] })) : undefined, kind === "voice" ? (_jsx("div", { className: "vld-anno-composer__voice", children: audioDataUrl ? (_jsx("audio", { src: audioDataUrl, controls: true })) : recording ? (_jsxs("button", { type: "button", className: "vlab-btn", onClick: stopRec, children: ["\u25A0 Stop \u00B7 ", formatDuration(elapsed)] })) : (_jsx("button", { type: "button", className: "vlab-btn", onClick: () => void startRec(), children: "\u25CF Record" })) })) : undefined, kind === "link" ? (candidates.length === 0 ? (_jsx("div", { className: "vld-anno-empty", children: "No other blocks in this collection to link." })) : (_jsx("select", { value: linkBlockId, onChange: (e) => setLinkBlockId(e.target.value), children: candidates.map((c) => (_jsx("option", { value: c.id, children: c.label }, c.id))) }))) : undefined, _jsx("textarea", { className: "vld-anno-composer__text", value: text, onChange: (e) => setText(e.target.value), placeholder: kind === "text" ? "Write a note…" : "Add a caption (optional)…", rows: kind === "text" ? 3 : 2 }), _jsxs("div", { className: "vld-anno-composer__actions", children: [_jsx("button", { type: "button", className: "vlab-btn vlab-btn--ghost", onClick: onCancel, children: "Cancel" }), _jsx("button", { type: "button", className: "vlab-btn", disabled: !canSave, onClick: save, children: "Save" })] })] }));
};
// --- Quick comment box (always-visible: write text, paste/drop images) ------
// Prototype lives in localStorage, so keep attachments bounded (quota ~5 MB).
const MAX_COMMENT_IMAGES = 12;
const MAX_COMMENT_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_COMMENT_TEXT_LENGTH = 8000;
function pastedImageId() {
    try {
        return crypto.randomUUID();
    }
    catch {
        return `img-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    }
}
/** Resolve the browser's (vendor-prefixed) SpeechRecognition constructor, if any. */
function getSpeechRecognitionCtor() {
    const w = window;
    return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}
/**
 * A persistent composer at the top of the annotation list: type a comment and
 * paste (or drag-drop) images straight into it. Enter submits, Shift+Enter adds
 * a newline. A submission becomes one annotation (text + any images), which then
 * shows up in the list below. Honors a pending region/quote reference via the
 * shared `onSubmit` handler.
 *
 * Push-to-talk: hold Space while the box is empty to dictate — interim words
 * stream in live, and releasing Space auto-transcribes the speech into a
 * comment (Web Speech API; no-op where unsupported).
 */
/** Max @-mention suggestions shown in the composer dropdown. */
const MAX_MENTION_SUGGESTIONS = 6;
/** The `@handle` token the caret is inside, if any (start-of-text/whitespace + `@`). */
function activeMentionToken(value, caret) {
    const before = value.slice(0, caret);
    const match = /(?:^|\s)@([a-zA-Z0-9_]*)$/.exec(before);
    if (!match)
        return undefined;
    return { query: match[1], start: caret - match[1].length - 1 };
}
const QuickCommentBox = ({ hasPending, onSubmit, variant = "panel", autoFocus = false, dimmed = false, style, onDismiss, collectionId, color, onColorChange, }) => {
    const [text, setText] = useState("");
    const [images, setImages] = useState([]);
    const [notice, setNotice] = useState();
    const [dragOver, setDragOver] = useState(false);
    // @-mention autocomplete: collaborators on the block's collection.
    const membersQ = useQuery({
        queryKey: ["vlab-data", "collection-members", collectionId],
        enabled: Boolean(collectionId),
        staleTime: 60_000,
        retry: false,
        queryFn: () => listCollectionMembers(collectionId),
    });
    const [mentionToken, setMentionToken] = useState(undefined);
    const [mentionHighlight, setMentionHighlight] = useState(0);
    const mentionSuggestions = mentionToken
        ? (membersQ.data ?? [])
            .filter((m) => m.username && m.username.toLowerCase().startsWith(mentionToken.query.toLowerCase()))
            .slice(0, MAX_MENTION_SUGGESTIONS)
        : [];
    const mentionMenuOpen = mentionToken !== undefined && mentionSuggestions.length > 0;
    // Push-to-talk dictation state. `listening` drives the UI; the refs hold the
    // live recognizer, whether Space is currently held (synchronously, ahead of
    // the `listening` state flush), and the final transcript accumulated so far.
    const [listening, setListening] = useState(false);
    const [dictationSupported] = useState(() => getSpeechRecognitionCtor() !== undefined);
    const textareaRef = useRef(null);
    const recognitionRef = useRef(undefined);
    const spaceHeldRef = useRef(false);
    const finalTranscriptRef = useRef("");
    // The recognizer's `onend` callback is created once per recording, so the
    // latest attachments are read through a ref rather than a stale closure.
    const imagesRef = useRef(images);
    imagesRef.current = images;
    // Mirror the box text too: on Space-release we commit exactly what the user
    // saw — including interim words the engine never marked "final" before stop.
    const textRef = useRef(text);
    textRef.current = text;
    const addFiles = (files) => {
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length === 0) {
            setNotice("Only image files can be attached.");
            return;
        }
        const freeSlots = MAX_COMMENT_IMAGES - images.length;
        if (freeSlots <= 0) {
            setNotice(`Up to ${MAX_COMMENT_IMAGES} images per comment.`);
            return;
        }
        const accepted = imageFiles.slice(0, freeSlots);
        if (accepted.length < imageFiles.length) {
            setNotice(`Up to ${MAX_COMMENT_IMAGES} images per comment.`);
        }
        for (const file of accepted) {
            if (file.size > MAX_COMMENT_IMAGE_BYTES) {
                setNotice(`"${file.name || "image"}" is too large (max 8 MB).`);
                continue;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const url = reader.result;
                // Guard again on commit in case several reads resolve concurrently.
                setImages((prev) => prev.length >= MAX_COMMENT_IMAGES ? prev : [...prev, { id: pastedImageId(), url }]);
            };
            reader.onerror = () => setNotice("Could not read that image.");
            reader.readAsDataURL(file);
        }
    };
    const onPaste = (e) => {
        const dt = e.clipboardData;
        if (!dt)
            return;
        const files = Array.from(dt.items)
            .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
            .map((it) => it.getAsFile())
            .filter((f) => f !== null);
        // No image on the clipboard → let the normal text paste happen.
        if (files.length === 0)
            return;
        e.preventDefault();
        setNotice(undefined);
        addFiles(files);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (files.length === 0)
            return;
        setNotice(undefined);
        addFiles(files);
    };
    const onDragOver = (e) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
            e.preventDefault();
            setDragOver(true);
        }
    };
    const removeImage = (id) => setImages((prev) => prev.filter((img) => img.id !== id));
    const canSubmit = text.trim().length > 0 || images.length > 0;
    const commit = (commentText, imgs) => {
        const trimmed = commentText.trim().slice(0, MAX_COMMENT_TEXT_LENGTH);
        if (trimmed.length === 0 && imgs.length === 0)
            return;
        onSubmit({
            kind: imgs.length > 0 ? "image" : "text",
            text: trimmed || undefined,
            imageDataUrls: imgs.length > 0 ? imgs.map((img) => img.url) : undefined,
            color,
        });
        setText("");
        setImages([]);
        setNotice(undefined);
        onColorChange?.(undefined);
    };
    const syncMentionToken = (value, caret) => {
        setMentionToken(activeMentionToken(value, caret));
        setMentionHighlight(0);
    };
    const insertMention = (member) => {
        const el = textareaRef.current;
        if (!el || !mentionToken || !member.username)
            return;
        const caret = el.selectionStart ?? text.length;
        const nextText = `${text.slice(0, mentionToken.start)}@${member.username} ${text.slice(caret)}`;
        const nextCaret = mentionToken.start + member.username.length + 2; // '@' + name + ' '
        setText(nextText);
        setMentionToken(undefined);
        requestAnimationFrame(() => {
            const node = textareaRef.current;
            if (!node)
                return;
            node.focus();
            node.setSelectionRange(nextCaret, nextCaret);
        });
    };
    const stopDictation = () => {
        const rec = recognitionRef.current;
        if (rec === undefined) {
            setListening(false);
            return;
        }
        try {
            rec.stop();
        }
        catch {
            // Already stopped — `onend` still settles the listening state.
        }
    };
    const startDictation = () => {
        const Ctor = getSpeechRecognitionCtor();
        if (Ctor === undefined) {
            setNotice("Voice dictation isn’t supported in this browser.");
            spaceHeldRef.current = false;
            return;
        }
        let rec;
        try {
            rec = new Ctor();
        }
        catch {
            setNotice("Couldn’t start voice dictation.");
            spaceHeldRef.current = false;
            return;
        }
        rec.lang = navigator.language || "en-US";
        rec.continuous = true;
        rec.interimResults = true;
        finalTranscriptRef.current = "";
        rec.onresult = (event) => {
            let interim = "";
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                const transcript = result?.[0]?.transcript ?? "";
                if (result?.isFinal === true)
                    finalTranscriptRef.current += `${transcript} `;
                else
                    interim += transcript;
            }
            const live = `${finalTranscriptRef.current}${interim}`.replace(/\s+/g, " ").trimStart();
            setText(live.slice(0, MAX_COMMENT_TEXT_LENGTH));
        };
        rec.onerror = (event) => {
            const code = event?.error;
            // Fatal errors: stop trying so `onend` settles without a restart loop.
            if (code === "not-allowed" || code === "service-not-allowed") {
                setNotice("Microphone access was denied — allow it to dictate.");
                spaceHeldRef.current = false;
            }
            else if (code === "audio-capture") {
                setNotice("No microphone was found.");
                spaceHeldRef.current = false;
            }
            else if (code !== undefined && code !== "no-speech" && code !== "aborted") {
                setNotice("Voice dictation error — try again.");
            }
        };
        rec.onend = () => {
            // Chrome ends a continuous session on its own after a pause or network
            // blip. If Space is still held, resume listening (the transcript so far is
            // preserved) instead of finalizing early.
            if (spaceHeldRef.current) {
                try {
                    rec.start();
                    return;
                }
                catch {
                    // Couldn't resume — fall through and settle.
                }
            }
            recognitionRef.current = undefined;
            setListening(false);
            // Commit exactly what the user saw — final words plus any trailing interim
            // the engine never marked final before stop() cut it off.
            const spoken = textRef.current.replace(/\s+/g, " ").trim();
            if (spoken.length > 0) {
                commit(spoken, imagesRef.current);
            }
            else {
                setText("");
            }
        };
        try {
            rec.start();
            recognitionRef.current = rec;
            setListening(true);
            setNotice(undefined);
        }
        catch {
            setNotice("Couldn’t start voice dictation.");
            spaceHeldRef.current = false;
        }
    };
    // Focus the box when a reference is pending, so after selecting a region you
    // can immediately type, paste, or hold Space to dictate a comment about it.
    // The rail box opts out (`autoFocus={false}`) while the in-situ box is open so
    // the two composers don't fight over focus.
    useEffect(() => {
        if (autoFocus && hasPending)
            textareaRef.current?.focus();
    }, [autoFocus, hasPending]);
    // Abort any in-flight recognition if the box unmounts (e.g. modal closed
    // mid-recording) so the mic is released.
    useEffect(() => {
        return () => {
            const rec = recognitionRef.current;
            if (rec !== undefined) {
                try {
                    rec.abort();
                }
                catch {
                    // ignore
                }
                recognitionRef.current = undefined;
            }
        };
    }, []);
    const onKeyDown = (e) => {
        // The @-mention dropdown owns the nav keys while it's open.
        if (mentionMenuOpen) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionHighlight((h) => (h + 1) % mentionSuggestions.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionHighlight((h) => (h - 1 + mentionSuggestions.length) % mentionSuggestions.length);
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                insertMention(mentionSuggestions[mentionHighlight]);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setMentionToken(undefined);
                return;
            }
        }
        if (e.key === " ") {
            // Dictating already: swallow Space (incl. auto-repeat) so it never leaks
            // into the transcript.
            if (spaceHeldRef.current || listening) {
                e.preventDefault();
                return;
            }
            // Start push-to-talk only from an empty box, so a real space mid-sentence
            // still types normally.
            if (e.repeat === false && dictationSupported && text.trim().length === 0) {
                e.preventDefault();
                e.stopPropagation();
                spaceHeldRef.current = true;
                startDictation();
            }
            return;
        }
        if (e.key === "Enter" && e.shiftKey === false) {
            e.preventDefault();
            commit(text, images);
        }
    };
    const onKeyUp = (e) => {
        if (e.key === " " && spaceHeldRef.current) {
            e.preventDefault();
            spaceHeldRef.current = false;
            stopDictation();
        }
        else {
            // Keep the @-mention token in sync as the caret moves (arrows, etc.).
            syncMentionToken(e.currentTarget.value, e.currentTarget.selectionStart ?? 0);
        }
    };
    return (_jsxs("div", { className: `vld-anno-quickbox vld-anno-quickbox--${variant}${dragOver ? " is-dragover" : ""}${listening ? " is-listening" : ""}${dimmed ? " is-dimmed" : ""}${color ? ` is-color-${color}` : ""}`, style: { position: "relative", ...style }, onDragOver: onDragOver, onDragLeave: () => setDragOver(false), onDrop: onDrop, children: [onDismiss ? (_jsx("button", { type: "button", className: "vld-anno-quickbox__dismiss", onClick: onDismiss, "aria-label": "Clear reference", children: "\u00D7" })) : undefined, images.length > 0 ? (_jsx("div", { className: "vld-anno-quickbox__thumbs", children: images.map((img) => (_jsxs("div", { className: "vld-anno-quickbox__thumb", children: [_jsx("img", { src: img.url, alt: "pasted attachment" }), _jsx("button", { type: "button", className: "vld-anno-quickbox__thumb-del", onClick: () => removeImage(img.id), "aria-label": "Remove image", children: "\u00D7" })] }, img.id))) })) : undefined, mentionMenuOpen ? (_jsx("ul", { className: "vld-anno-quickbox__mentions", role: "listbox", "aria-label": "Mention a collaborator", children: mentionSuggestions.map((m, i) => (_jsx("li", { role: "option", "aria-selected": i === mentionHighlight, children: _jsxs("button", { type: "button", className: `vld-anno-quickbox__mention${i === mentionHighlight ? " is-active" : ""}`, onMouseDown: (e) => {
                            // mousedown (not click) so the textarea keeps focus.
                            e.preventDefault();
                            insertMention(m);
                        }, children: ["@", m.username] }) }, m.accountId))) })) : undefined, _jsx("textarea", { ref: textareaRef, className: "vld-anno-quickbox__text", value: text, onChange: (e) => {
                    setText(e.target.value);
                    syncMentionToken(e.target.value, e.target.selectionStart ?? e.target.value.length);
                }, onClick: (e) => syncMentionToken(e.currentTarget.value, e.currentTarget.selectionStart ?? 0), onPaste: onPaste, onKeyDown: onKeyDown, onKeyUp: onKeyUp, placeholder: listening
                    ? "Listening… release Space to save"
                    : hasPending
                        ? "Comment on the reference — write, paste, or hold Space to talk…"
                        : "Write a comment, paste an image, or hold Space to talk…", rows: 2 }), notice ? _jsx("div", { className: "vld-anno-quickbox__notice", children: notice }) : undefined, _jsxs("div", { className: "vld-anno-quickbox__foot", children: [_jsx("button", { type: "button", className: "vlab-btn", disabled: canSubmit === false, onClick: () => commit(text, images), children: "Comment" }), _jsx("div", { className: "vld-anno-quickbox__colors", role: "group", "aria-label": "Comment colour", children: ["green", "red"].map((c) => (_jsx("button", { type: "button", className: `vld-anno-quickbox__color vld-anno-quickbox__color--${c}${color === c ? " is-active" : ""}`, "aria-pressed": color === c, "aria-label": `${c} flag`, title: `Flag this comment ${c}`, onClick: () => onColorChange?.(color === c ? undefined : c) }, c))) }), dictationSupported ? (_jsx("span", { className: `vld-anno-quickbox__dictate${listening ? " is-on" : ""}`, "aria-live": "polite", children: listening ? "● Listening… release Space" : "" })) : undefined] })] }));
};
// --- Annotation card --------------------------------------------------------
const AnnotationCard = ({ annotation: a, refNumber, hovered, authorName, onHover, onOpenLink, onRemove }) => {
    // Prefer the multi-image array (quick comment box); fall back to the legacy
    // single image set by the Image composer.
    const cardImages = a.imageDataUrls?.length
        ? a.imageDataUrls
        : a.imageDataUrl
            ? [a.imageDataUrl]
            : [];
    return (_jsxs("div", { className: `vld-anno-card${hovered ? " is-hover" : ""}${a.color ? ` is-color-${a.color}` : ""}`, onMouseEnter: () => onHover(a.id), onMouseLeave: () => onHover(undefined), children: [_jsxs("div", { className: "vld-anno-card__top", children: [_jsx("span", { className: "vld-anno-card__author", children: authorName }), _jsx("span", { className: "vld-anno-card__time", children: formatClockTime(a.createdAt) }), refNumber !== undefined ? (_jsxs("span", { className: "vld-anno-card__pin", children: ["#", refNumber] })) : undefined, _jsx("button", { type: "button", className: "vlab-x vld-anno-card__del", onClick: () => onRemove(a.id), "aria-label": "Delete annotation", children: "\u00D7" })] }), a.quote ? _jsxs("div", { className: "vld-anno-card__quote", children: ["\u201C", a.quote, "\u201D"] }) : undefined, a.text ? _jsx("div", { className: "vld-anno-card__text", children: a.text }) : undefined, cardImages.map((src, i) => (_jsx("div", { className: "vld-anno-card__imgwrap", children: _jsx("img", { className: "vld-anno-card__img", src: src, alt: "annotation attachment" }) }, `${a.id}-img-${i}`))), a.kind === "voice" && a.audioDataUrl ? (_jsxs("div", { className: "vld-anno-card__voice", children: [_jsx("audio", { src: a.audioDataUrl, controls: true }), _jsx("span", { className: "vld-anno-card__dur", children: formatDuration(a.audioDurationMs) })] })) : undefined, a.kind === "link" && a.linkBlockId ? (_jsxs("button", { type: "button", className: "vld-anno-card__link", onClick: () => onOpenLink(a.linkBlockId), children: ["\u2192 ", a.linkLabel ?? "Linked block"] })) : undefined] }));
};
// --- Expanded block view with annotations ----------------------------------
/**
 * The block's actual content inside the modal stage — the image, the text
 * card, or the post-it. A click that lands anywhere else on the stage (the
 * letterboxing / padding around it) is an "outside" click and closes the modal.
 */
const MODAL_CONTENT_SELECTOR = ".vld-anno-imgwrap, .vld-anno-text, .vld-modal-postit";
// In-situ comment box placement ----------------------------------------------
//
// The floating composer is anchored to the freshly drawn region inside the
// image wrapper. We prefer placing it just below the selection; if there isn't
// room below we flip above; if neither fits we overlay near the selection,
// clamped to the image box. (TODO: smarter placement when the image is tiny —
// e.g. spill into the stage padding instead of overlaying the picture.)
const INSITU_BOX_W = 320;
const INSITU_GAP = 10;
/** Rough composer height, used only to choose above-vs-below; layout is exact. */
const INSITU_EST_H = 132;
/** Rough hover-card height — shorter than the composer, so it flips later. */
const HOVER_CARD_EST_H = 72;
function computeInSituStyle(region, wrapEl, estHeight = INSITU_EST_H) {
    const rect = wrapEl.getBoundingClientRect();
    const wrapW = rect.width;
    const wrapH = rect.height;
    const boxW = Math.max(180, Math.min(INSITU_BOX_W, wrapW - INSITU_GAP * 2));
    const regionTopPx = region.y * wrapH;
    const regionBottomPx = (region.y + region.h) * wrapH;
    const regionLeftPx = region.x * wrapW;
    // Keep the box inside the image horizontally, aligned to the region's left.
    const leftPx = Math.max(INSITU_GAP, Math.min(regionLeftPx, wrapW - boxW - INSITU_GAP));
    const spaceBelow = wrapH - regionBottomPx;
    const spaceAbove = regionTopPx;
    const need = estHeight + INSITU_GAP;
    if (spaceBelow >= need) {
        return {
            placement: "below",
            style: { left: leftPx, top: regionBottomPx + INSITU_GAP, width: boxW },
        };
    }
    if (spaceAbove >= need) {
        // Anchor by the bottom edge so the box can grow upward without measuring it.
        return {
            placement: "above",
            style: { left: leftPx, bottom: wrapH - regionTopPx + INSITU_GAP, width: boxW },
        };
    }
    // No clear room either side — overlay just below the region top, clamped in.
    const topPx = Math.max(INSITU_GAP, Math.min(regionBottomPx + INSITU_GAP, wrapH - estHeight - INSITU_GAP));
    return { placement: "overlay", style: { left: leftPx, top: topPx, width: boxW } };
}
const BlockAnnotateModal = ({ block, collectionName, candidates, onClose, onOpenLink }) => {
    const realMedia = blockMediaUrl(block);
    const shot = useBlockShot(block.id);
    const isLink = block.type === "link";
    const isText = realMedia === undefined && block.type === "text";
    // Stage media priority. For website (link) blocks the scraped 800×800
    // screenshot is the whole point of *opening* them — so it wins over a
    // favicon/OG thumbnail (you see the page, not its icon), with region-
    // annotation support, falling back to that thumbnail then the link post-it.
    // Every other block keeps its own image/video media; the shot only stands in
    // when there's none, and text always keeps its text stage.
    const media = isLink ? shot ?? realMedia : realMedia ?? (isText ? undefined : shot);
    const isImage = media !== undefined;
    const linkUrl = blockLinkUrl(block);
    const label = useBlockDisplayLabel(block);
    // Full-width view: let an image block fill the stage width (and scroll) rather
    // than fit-to-viewport. Only meaningful for the image stage; persisted.
    const [fullWidth, setFullWidth] = useState(loadBlockFullWidth);
    const toggleFullWidth = () => {
        setFullWidth((prev) => {
            const next = !prev;
            saveBlockFullWidth(next);
            return next;
        });
    };
    // The stage's scrollbar thumb is transparent at rest and only paints while the
    // user is actively scrolling — flagged here and cleared a beat after scrolling
    // stops (no native CSS "show-while-scrolling" exists, so we drive it).
    const [stageScrolling, setStageScrolling] = useState(false);
    const scrollIdleRef = useRef(undefined);
    const onStageScroll = () => {
        setStageScrolling(true);
        if (scrollIdleRef.current !== undefined)
            window.clearTimeout(scrollIdleRef.current);
        scrollIdleRef.current = window.setTimeout(() => setStageScrolling(false), 700);
    };
    useEffect(() => {
        return () => {
            if (scrollIdleRef.current !== undefined)
                window.clearTimeout(scrollIdleRef.current);
        };
    }, []);
    // Website blocks get a stage view toggle: the link card vs. a captured
    // full-page screenshot (urlbox `full_page`, stored to the block). Selecting
    // the screenshot tab captures on first use; the render is remembered after.
    const [stageView, setStageView] = useState("card");
    const fullPageShot = useFullPageShot(block.id);
    const [capturingFull, setCapturingFull] = useState(false);
    const [fullShotError, setFullShotError] = useState(undefined);
    const [fullLoaded, setFullLoaded] = useState(false);
    useEffect(() => setFullLoaded(false), [fullPageShot]);
    const showFullPage = () => {
        setStageView("fullpage");
        if (fullPageShot || !linkUrl || capturingFull)
            return;
        setCapturingFull(true);
        setFullShotError(undefined);
        captureFullPageShot(block.id, linkUrl)
            .catch((e) => setFullShotError(e instanceof Error ? e.message : "Capture failed"))
            .finally(() => setCapturingFull(false));
    };
    const { annotations: localAnnotations, add, remove } = useBlockAnnotations(block.id);
    const { account } = useAccountMe();
    const authorName = account?.username ? `@${account.username}` : "You";
    // Comments live on the backend now (durable, multi-user, mention-aware);
    // other annotation kinds (voice/image/link/region) remain the local
    // prototype. Fetch the block's backend comments and merge them with the
    // local non-text annotations into the one list the rail renders.
    const queryClient = useQueryClient();
    const backendCommentsKey = ["vlab-data", "block-comments", block.id];
    const backendCommentsQ = useQuery({
        queryKey: backendCommentsKey,
        queryFn: () => listUserAnnotations(block.id),
        staleTime: 30_000,
        retry: false,
    });
    // Backend comments carry an ownerId, not a name — resolve via collection members.
    const commentMembersQ = useQuery({
        queryKey: ["vlab-data", "collection-members", block.collectionId],
        enabled: Boolean(block.collectionId),
        staleTime: 60_000,
        retry: false,
        queryFn: () => listCollectionMembers(block.collectionId),
    });
    const ownerName = useMemo(() => {
        const map = new Map();
        for (const m of commentMembersQ.data ?? []) {
            if (m.username)
                map.set(m.accountId, `@${m.username}`);
        }
        return map;
    }, [commentMembersQ.data]);
    // Backend text comments + every local annotation. New text comments are
    // backend-only (see handleSave), so local text rows are only legacy/offline
    // fallbacks — we keep showing them rather than risk hiding a comment that
    // never made it to the server.
    const annotations = useMemo(() => {
        const merged = [];
        for (const ua of backendCommentsQ.data ?? []) {
            if (ua.type !== "comment")
                continue;
            // `color` ships with the comment-color backend (separate branch); read it
            // defensively until this branch picks up the updated CommentContent type.
            const color = ua.content.color;
            merged.push({
                id: ua.id,
                kind: "text",
                createdAt: new Date(ua.createdAt).getTime(),
                text: ua.content.body,
                ownerId: ua.ownerId,
                color,
            });
        }
        for (const a of localAnnotations)
            merged.push(a);
        return merged;
    }, [backendCommentsQ.data, localAnnotations]);
    // The annotations layer is gated behind the debug toggle: when off, this modal
    // is just a large block viewer — no panel, no region/quote overlays, and the
    // reference gestures (image drag / text select) are inert.
    const [showAnnotations] = useShowAnnotations();
    const [composerKind, setComposerKind] = useState(undefined);
    const [pending, setPending] = useState(undefined);
    const [hoveredId, setHoveredId] = useState(undefined);
    // Selected comment colour, lifted here so the reference outline can match it.
    const [commentColor, setCommentColor] = useState(undefined);
    const imgWrapRef = useRef(null);
    const dragRef = useRef(undefined);
    const [draft, setDraft] = useState(undefined);
    const ordered = useMemo(() => [...annotations].sort((a, b) => a.createdAt - b.createdAt), [annotations]);
    // Number the referenced annotations (region on image, quote on text).
    const refNumbers = useMemo(() => {
        const map = new Map();
        let n = 0;
        for (const a of ordered) {
            if ((isImage && a.region) || (isText && a.quote))
                map.set(a.id, ++n);
        }
        return map;
    }, [ordered, isImage, isText]);
    const regionAnnos = useMemo(() => (showAnnotations ? ordered.filter((a) => a.region) : []), [ordered, showAnnotations]);
    // In-situ composer: show a floating comment box anchored to a freshly drawn
    // region (image stages only). Re-measure when the region changes or the
    // window resizes so it tracks the image box.
    const inSituActive = isImage && pending?.kind === "region" && showAnnotations;
    const [inSitu, setInSitu] = useState(undefined);
    useLayoutEffect(() => {
        if (inSituActive === false) {
            setInSitu(undefined);
            return;
        }
        const measure = () => {
            const el = imgWrapRef.current;
            if (!el || pending?.kind !== "region")
                return;
            setInSitu(computeInSituStyle(pending.region, el));
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [inSituActive, pending]);
    // Hover preview: while a saved region's box (or its rail card) is hovered,
    // float a read-only card with its comment above/below the region, reusing the
    // in-situ placement. Suppressed while drawing or composing so it never fights
    // the composer for the same spot.
    const hoveredRegionAnno = useMemo(() => (hoveredId ? regionAnnos.find((a) => a.id === hoveredId) : undefined), [hoveredId, regionAnnos]);
    const hoverCardActive = isImage &&
        showAnnotations &&
        draft === undefined &&
        pending === undefined &&
        hoveredRegionAnno !== undefined &&
        annotationHasPreview(hoveredRegionAnno);
    const [hoverCard, setHoverCard] = useState(undefined);
    useLayoutEffect(() => {
        const region = hoveredRegionAnno?.region;
        if (hoverCardActive === false || !region) {
            setHoverCard(undefined);
            return;
        }
        const measure = () => {
            const el = imgWrapRef.current;
            if (!el)
                return;
            setHoverCard(computeInSituStyle(region, el, HOVER_CARD_EST_H));
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [hoverCardActive, hoveredRegionAnno]);
    const pointToNorm = (clientX, clientY) => {
        const el = imgWrapRef.current;
        if (!el)
            return { x: 0, y: 0 };
        const r = el.getBoundingClientRect();
        return {
            x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
            y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
        };
    };
    const onImgMouseDown = (e) => {
        if (!isImage)
            return;
        e.preventDefault();
        // Starting a fresh region erases any prior selection — the old box should
        // not linger behind the new one (and a too-small redraw shouldn't leave it
        // standing). On an image stage `pending` is only ever a region.
        setPending(undefined);
        dragRef.current = pointToNorm(e.clientX, e.clientY);
        setDraft({ ...dragRef.current, w: 0, h: 0 });
    };
    useEffect(() => {
        if (!isImage)
            return;
        const move = (e) => {
            const start = dragRef.current;
            if (!start)
                return;
            const cur = pointToNorm(e.clientX, e.clientY);
            setDraft({
                x: Math.min(start.x, cur.x),
                y: Math.min(start.y, cur.y),
                w: Math.abs(cur.x - start.x),
                h: Math.abs(cur.y - start.y),
            });
        };
        const up = () => {
            const start = dragRef.current;
            dragRef.current = undefined;
            // Minimum drag to count as a real selection. `draft` is normalized to the
            // image's displayed size, so a fixed fraction (the old `0.02`) scaled with
            // the image — on a large or very tall image (e.g. a full-page screenshot)
            // a perfectly normal box could fall under 2% of the height and be silently
            // dropped. Convert a ~5px absolute floor into that normalized space so the
            // gate is image-size-independent.
            const r = imgWrapRef.current?.getBoundingClientRect();
            const minW = r && r.width > 0 ? 5 / r.width : 0.01;
            const minH = r && r.height > 0 ? 5 / r.height : 0.01;
            setDraft((d) => {
                if (start && d && d.w > minW && d.h > minH)
                    setPending({ kind: "region", region: d });
                return undefined;
            });
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
        };
    }, [isImage]);
    // Backing out of an in-progress selection should cancel the selection, not
    // close the modal. While a region/quote is drawn (`draft`) or pending a
    // comment (`pending`), Escape and ⌘Z / Ctrl-Z ("undo") clear it instead. We
    // listen on the capture phase and stop the event so the screen-level
    // Esc-to-close handler never fires; ⌘Z is left alone while typing so text
    // undo in the comment box still works. With no selection this effect attaches
    // nothing, so Escape closes the modal as before.
    useEffect(() => {
        if (draft === undefined && pending === undefined)
            return;
        const onKey = (e) => {
            const isEscape = e.key === "Escape";
            const isUndo = (e.metaKey || e.ctrlKey) &&
                e.shiftKey === false &&
                e.altKey === false &&
                e.key.toLowerCase() === "z";
            if (isEscape === false && isUndo === false)
                return;
            if (isUndo) {
                const target = e.target;
                const typing = target?.isContentEditable === true ||
                    target?.matches?.("input, textarea, select, [contenteditable='true']") === true;
                if (typing)
                    return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            setDraft(undefined);
            setPending(undefined);
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [draft, pending]);
    const onTextMouseUp = () => {
        if (!isText)
            return;
        const q = window.getSelection()?.toString().trim();
        if (q)
            setPending({ kind: "quote", quote: q });
    };
    const handleSave = (a) => {
        const next = { ...a };
        if (pending?.kind === "region")
            next.region = pending.region;
        if (pending?.kind === "quote")
            next.quote = pending.quote;
        const body = next.text?.trim();
        // Pure text comments are backend-only now (no region/quote, which the
        // backend "whole" comment can't carry). Everything else — voice/image/link,
        // or a text note anchored to a region/quote — stays the local prototype.
        const isPlainTextComment = a.kind === "text" && !next.region && !next.quote;
        if (isPlainTextComment && body) {
            void createCommentAnnotation(block.id, body, next.color)
                .then(() => backendCommentsQ.refetch())
                .catch(() => {
                // Backend unreachable: fall back to a local comment so the note
                // isn't lost (it just won't be shared / mention-parsed).
                add(next);
            });
        }
        else {
            add(next);
        }
        setComposerKind(undefined);
        setPending(undefined);
    };
    // Comments live on the backend; other annotation kinds are local. Route the
    // delete by id namespace (`uan_` = backend) so each goes to the right store.
    const handleRemove = (id) => {
        if (id.startsWith("uan_")) {
            // Optimistic: drop it from the cache immediately so the row vanishes
            // before the server replies. Roll back (refetch) only if the delete fails.
            queryClient.setQueryData(backendCommentsKey, (old) => old ? old.filter((ua) => ua.id !== id) : old);
            void deleteUserAnnotation(id).catch(() => {
                void backendCommentsQ.refetch();
            });
        }
        else {
            remove(id); // localStorage — already instant
        }
    };
    const textContent = isText ? blockSnippet(block) || blockCaption(block) : "";
    const textNodes = useMemo(() => buildTextSegments(textContent, showAnnotations ? ordered.filter((a) => a.quote) : [], refNumbers), [textContent, ordered, refNumbers, showAnnotations]);
    // Click the bare stage (anywhere that isn't the image / text / post-it) to
    // close. We require the press to START outside the content too, so a region
    // drag on the image or a text selection — gestures that can release over the
    // empty stage — never dismiss the modal by accident.
    const pressedOutsideRef = useRef(false);
    const hitsContent = (target) => target?.closest(MODAL_CONTENT_SELECTOR) != undefined;
    const onStageMouseDown = (e) => {
        pressedOutsideRef.current = hitsContent(e.target) === false;
    };
    const onStageClick = (e) => {
        if (pressedOutsideRef.current && hitsContent(e.target) === false)
            onClose();
        pressedOutsideRef.current = false;
    };
    return (_jsxs("div", { className: `vlab-modal vld-anno-modal${showAnnotations ? "" : " vld-anno-modal--bare"}${fullWidth && isImage ? " vld-anno-modal--fullwidth" : ""}`, role: "dialog", "aria-modal": "true", children: [_jsx("button", { type: "button", className: "vlab-modal__backdrop", "aria-label": "Close", onClick: onClose }), _jsxs("div", { className: "vlab-modal__frame", children: [isImage ? (_jsx("button", { type: "button", className: `vld-fullwidth-toggle${fullWidth ? " is-on" : ""}`, onClick: toggleFullWidth, "aria-pressed": fullWidth, title: "Toggle full-width / fit-to-screen", children: "Scrollable" })) : undefined, _jsx("button", { type: "button", className: "vlab-modal__close", onClick: onClose, "aria-label": "Close", children: "\u00D7" }), isLink ? (_jsxs("div", { className: "vld-stage-tabs", children: [_jsx("button", { type: "button", className: `vld-stage-tab${stageView === "card" ? " is-active" : ""}`, onClick: () => setStageView("card"), children: "Link" }), _jsx("button", { type: "button", className: `vld-stage-tab${stageView === "fullpage" ? " is-active" : ""}`, onClick: showFullPage, children: "Full Screenshot" })] })) : undefined, _jsx("div", { className: `vlab-modal__stage${stageScrolling ? " is-scrolling" : ""}`, onMouseDown: onStageMouseDown, onClick: onStageClick, onScroll: onStageScroll, children: isLink && stageView === "fullpage" ? (_jsx("div", { className: "vld-fullpage", onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: fullShotError ? (_jsxs("div", { className: "vld-fullpage__msg", children: ["Couldn\u2019t capture: ", fullShotError] })) : fullPageShot ? (_jsxs(_Fragment, { children: [fullLoaded ? undefined : (_jsx("div", { className: "vld-fullpage__msg", children: "Rendering full page\u2026 (first capture can take a few seconds)" })), _jsx("img", { className: "vld-fullpage__img", src: fullPageShot, alt: `Full-page screenshot of ${linkUrl ?? "page"}`, draggable: false, onLoad: () => setFullLoaded(true), onError: () => setFullShotError("The screenshot render failed to load.") })] })) : (_jsx("div", { className: "vld-fullpage__msg", children: "Preparing capture\u2026" })) })) : isImage ? (_jsxs("div", { className: "vld-anno-imgwrap", ref: imgWrapRef, onMouseDown: showAnnotations ? onImgMouseDown : undefined, children: [_jsx("img", { className: "vld-anno-img", src: media, alt: block.title ?? "", draggable: false }), regionAnnos.map((a) => (_jsx("div", { className: `vld-anno-region${hoveredId === a.id ? " is-hover" : ""}${a.color ? ` is-color-${a.color}` : ""}`, style: a.region ? rectStyle(a.region) : undefined, onMouseEnter: () => setHoveredId(a.id), onMouseLeave: () => setHoveredId(undefined), children: _jsxs("span", { className: "vld-anno-region__pin", children: ["#", refNumbers.get(a.id)] }) }, a.id))), draft ? (_jsx("div", { className: "vld-anno-region vld-anno-region--draft", style: rectStyle(draft) })) : undefined, pending?.kind === "region" ? (_jsx("div", { className: `vld-anno-region vld-anno-region--pending${commentColor ? ` is-color-${commentColor}` : ""}`, style: rectStyle(pending.region) })) : undefined, inSituActive && inSitu ? (_jsx("div", { className: `vld-anno-insitu vld-anno-insitu--${inSitu.placement}`, style: inSitu.style, 
                                    // Keep clicks/drags inside the card from starting a new region
                                    // selection or closing the modal.
                                    onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: _jsx(QuickCommentBox, { variant: "insitu", hasPending: true, autoFocus: true, onSubmit: handleSave, onDismiss: () => setPending(undefined), collectionId: block.collectionId, color: commentColor, onColorChange: setCommentColor }) })) : undefined, hoverCardActive && hoverCard && hoveredRegionAnno ? (_jsx(AnnotationHoverCard, { text: hoveredRegionAnno.text, images: annotationPreviewImages(hoveredRegionAnno), placement: hoverCard.placement, style: hoverCard.style, variant: "modal", refNumber: refNumbers.get(hoveredRegionAnno.id), authorName: authorName })) : undefined] })) : isText ? (_jsx("div", { className: "vld-anno-textwrap", children: _jsx("div", { className: "vld-anno-text", onMouseUp: showAnnotations ? onTextMouseUp : undefined, style: postitTint(block.id), children: textNodes }) })) : (_jsx("div", { className: "vlab-modal__postit-wrap", children: _jsx(BlockBody, { block: block, large: true }) })) }), showAnnotations ? (_jsxs("aside", { className: "vlab-modal__panel vld-anno-panel", children: [_jsxs("div", { className: "vld-anno-headbar", children: [_jsx("div", { className: "vlab-modal__title", children: label }), _jsxs("div", { className: "vld-anno-sub", children: ["# ", collectionName, " \u00B7 ", block.type, " \u00B7 ", formatDate(block.capturedAt)] }), linkUrl ? (_jsxs("a", { className: "vld-anno-visit", href: linkUrl, target: "_blank", rel: "noreferrer", children: ["Visit link ", _jsx("span", { "aria-hidden": "true", children: "\u2192" })] })) : undefined] }), pending === undefined && isText ? (_jsx("div", { className: "vld-anno-hint", children: "Select text to reference a quote." })) : undefined, _jsx(QuickCommentBox, { hasPending: pending !== undefined, onSubmit: handleSave, autoFocus: inSituActive === false, dimmed: inSituActive, collectionId: block.collectionId, color: commentColor, onColorChange: setCommentColor }), composerKind ? (_jsx(AnnotationComposer, { kind: composerKind, candidates: candidates, onCancel: () => setComposerKind(undefined), onSave: handleSave })) : undefined, _jsx("div", { className: "vld-anno-list", children: ordered.length === 0 ? (_jsx("div", { className: "vld-anno-empty" })) : (ordered.map((a) => (_jsx(AnnotationCard, { annotation: a, refNumber: refNumbers.get(a.id), hovered: hoveredId === a.id, authorName: a.ownerId && a.ownerId !== account?.id
                                        ? ownerName.get(a.ownerId) ?? "Someone"
                                        : authorName, onHover: setHoveredId, onOpenLink: onOpenLink, onRemove: handleRemove }, a.id)))) })] })) : undefined] })] }));
};
// ---------------------------------------------------------------------------
// Sidebar (groups → collections, with quick-filter)
// ---------------------------------------------------------------------------
/** One collection row: a select target plus a pin/unpin toggle on the right. */
const ChannelRow = ({ collection: c, active, highlighted = false, pinned, onSelect, onTogglePin }) => (_jsxs("div", { className: `vld-chrow${pinned ? " is-pinned" : ""}`, children: [_jsxs("a", { href: collectionHref(c.id), className: `vlab-sidebar__item vlab-sidebar__item--child vld-chrow__select${active ? " is-active" : ""}${highlighted ? " is-kbd" : ""}`, onClick: (e) => {
                if (!isPlainClick(e))
                    return;
                e.preventDefault();
                onSelect(c.id);
            }, title: collectionTitle(c), children: [_jsx("span", { className: "vlab-sidebar__glyph", children: "#" }), _jsx("span", { className: "vlab-sidebar__item-name", children: collectionTitle(c) }), typeof c.blockCount === "number" ? (_jsx("span", { className: "vlab-sidebar__badge", children: c.blockCount })) : undefined] }), _jsx("button", { type: "button", className: "vld-pin", onClick: () => onTogglePin(c.id), title: pinned ? "Unpin collection" : "Pin collection", "aria-label": pinned ? "Unpin collection" : "Pin collection", "aria-pressed": pinned, children: _jsx(StarIcon, {}) })] }));
/**
 * "Latest Activity" tab, pinned to the top of the nav above the favorites. A
 * single nav row that swaps the main panel for the activity feed.
 */
const ActivityTab = ({ active, onSelect }) => (_jsx("div", { className: "vlab-sidebar__section", children: _jsxs("a", { href: activityHref(), className: `vlab-sidebar__item vld-activity-tab${active ? " is-active" : ""}`, onClick: (e) => {
            if (!isPlainClick(e))
                return;
            e.preventDefault();
            onSelect();
        }, title: "Latest Activity", children: [_jsx("span", { className: "vlab-sidebar__glyph", children: _jsx(BellIcon, {}) }), _jsx("span", { className: "vlab-sidebar__item-name", children: "Latest Activity" })] }) }));
/**
 * Flat "Pinned" section above the group tree. Lists every pinned collection
 * across all groups, in the same order they appear in the (already filtered,
 * recency-sorted) tree. Renders nothing when no pins are present.
 */
const PinnedSection = ({ projects, pinnedIds, selection, onSelectCollection, onTogglePin }) => {
    if (pinnedIds.size === 0)
        return undefined;
    const pinned = [];
    for (const p of projects) {
        for (const c of p.channels)
            if (pinnedIds.has(c.id))
                pinned.push(c);
    }
    if (pinned.length === 0)
        return undefined;
    return (_jsxs("div", { className: "vlab-sidebar__section", children: [_jsx("div", { className: "vlab-sidebar__section-label vld-pinned-label", children: "Favorites" }), pinned.map((c) => (_jsx(ChannelRow, { collection: c, active: selection?.kind === "collection" && selection.id === c.id, pinned: true, onSelect: onSelectCollection, onTogglePin: onTogglePin }, c.id)))] }));
};
/**
 * Create-a-channel popup: a name field and a group picker. On submit it creates
 * a layer-1 collection under the chosen group, then hands the new row back to
 * the caller (which refreshes the lists and opens it). No border-radius / shadow
 * per the alpha design rules — the global reset zeroes radius; we add no shadow.
 */
const NewChannelModal = ({ open, groups, defaultGroupId, onClose, onCreated }) => {
    const [name, setName] = useState("");
    const [groupId, setGroupId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(undefined);
    const nameRef = useRef(null);
    // Reset to a clean slate each time it opens, defaulting the group to the
    // caller's hint (the current group/parent) or the first available group.
    useEffect(() => {
        if (!open)
            return;
        setName("");
        setError(undefined);
        setSubmitting(false);
        setGroupId(defaultGroupId ?? groups[0]?.id ?? "");
        const id = window.setTimeout(() => nameRef.current?.focus(), 0);
        return () => window.clearTimeout(id);
    }, [open, defaultGroupId, groups]);
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);
    if (!open)
        return undefined;
    const trimmed = name.trim();
    const canSubmit = trimmed.length > 0 && groupId !== "" && !submitting;
    const submit = async () => {
        if (!canSubmit)
            return;
        setSubmitting(true);
        setError(undefined);
        try {
            const collection = await createChildCollection(groupId, trimmed);
            onCreated(collection);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn’t create the channel.");
            setSubmitting(false);
        }
    };
    return (_jsx("div", { className: "vld-newchan-overlay", onMouseDown: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, children: _jsxs("div", { className: "vld-newchan", role: "dialog", "aria-modal": "true", "aria-label": "New channel", children: [_jsxs("div", { className: "vld-newchan__head", children: [_jsx("span", { className: "vld-newchan__title", children: "New channel" }), _jsx("button", { type: "button", className: "vld-newchan__close", onClick: onClose, "aria-label": "Close", children: "\u00D7" })] }), _jsxs("label", { className: "vld-newchan__field", children: [_jsx("span", { className: "vld-newchan__label", children: "Name" }), _jsx("input", { ref: nameRef, className: "vld-newchan__input", type: "text", value: name, placeholder: "Channel name", onChange: (e) => setName(e.target.value), onKeyDown: (e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    void submit();
                                }
                            } })] }), _jsxs("label", { className: "vld-newchan__field", children: [_jsx("span", { className: "vld-newchan__label", children: "Add to group" }), _jsxs("select", { className: "vld-newchan__select", value: groupId, onChange: (e) => setGroupId(e.target.value), children: [groups.length === 0 ? _jsx("option", { value: "", children: "No groups available" }) : undefined, groups.map((g) => (_jsx("option", { value: g.id, children: g.title }, g.id)))] })] }), error ? _jsx("div", { className: "vld-newchan__error", children: error }) : undefined, _jsxs("div", { className: "vld-newchan__actions", children: [_jsx("button", { type: "button", className: "vlab-btn vlab-btn--ghost", onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "vlab-btn", disabled: !canSubmit, onClick: () => void submit(), children: submitting ? "Creating…" : "Create channel" })] })] }) }));
};
const DataSidebar = ({ projects, selection, collapsed, onToggleCollapse, onSelectCollection, onSelectActivity, onOpenSearch, onNewChannel, pinnedIds, onTogglePin, }) => (_jsxs("aside", { className: `vlab-sidebar${collapsed ? " vlab-sidebar--collapsed" : ""}`, children: [_jsx("div", { className: "vlab-sidebar__head", children: _jsxs("div", { className: "vlab-sidebar__head-top", children: [_jsxs("button", { type: "button", className: "vlab-sidebar__workspace", title: "Putty", children: [_jsx("span", { className: "vlab-sidebar__mark", children: _jsx(GhostMark, {}) }), _jsx("span", { className: "vlab-sidebar__ws-name", children: "Putty" })] }), _jsx("button", { type: "button", className: "vlab-sidebar__collapse", onClick: onToggleCollapse, title: collapsed ? "Expand sidebar" : "Collapse sidebar", "aria-label": collapsed ? "Expand sidebar" : "Collapse sidebar", children: collapsed ? "»" : "«" })] }) }), _jsx("div", { className: "vld-sidebar-search", children: _jsxs("button", { type: "button", className: "vld-sidebar-search__btn", onClick: onOpenSearch, title: "Search (\u2318G)", "aria-label": "Open search", children: [_jsx(SearchIcon, { className: "vld-sidebar-search__icon" }), _jsx("span", { className: "vld-sidebar-search__label", children: "Search" })] }) }), _jsx("div", { className: "vlab-sidebar__section", children: _jsxs("button", { type: "button", className: "vlab-sidebar__item vld-new-channel", onClick: onNewChannel, title: "New channel", children: [_jsx("span", { className: "vlab-sidebar__glyph vld-new-channel__glyph", children: "+" }), _jsx("span", { className: "vlab-sidebar__item-name", children: "New channel" })] }) }), _jsx(ActivityTab, { active: selection?.kind === "activity", onSelect: onSelectActivity }), _jsx("div", { className: "vlab-sidebar__scroll", children: _jsx(PinnedSection, { projects: projects, pinnedIds: pinnedIds, selection: selection, onSelectCollection: onSelectCollection, onTogglePin: onTogglePin }) })] }));
// ---------------------------------------------------------------------------
// Group landing (collections as channel cards)
// ---------------------------------------------------------------------------
const GroupView = ({ entry, onSelectCollection, }) => (_jsxs(_Fragment, { children: [_jsxs("div", { className: "vlab-collhead", children: [_jsx("div", { className: "vlab-collhead__top", children: _jsx("div", { className: "vlab-crumb", children: _jsx("span", { className: "vlab-crumb__seg vlab-crumb__seg--active", children: groupTitle(entry.group) }) }) }), _jsx("div", { className: "vlab-collhead__stats", children: _jsxs("span", { children: [_jsx("span", { className: "vlab-collhead__stat-k", children: entry.channels.length }), " collections"] }) })] }), _jsx("div", { className: "vlab-panel", children: _jsxs("section", { className: "vlab-projsec", children: [_jsx("h3", { className: "vlab-projsec__h", children: "Collections" }), entry.channels.length === 0 ? (_jsx("div", { className: "vlab-empty", children: "This group has no collections yet." })) : (_jsx("div", { className: "vlab-channels", children: entry.channels.map((c) => (_jsxs("a", { href: collectionHref(c.id), className: "vlab-chcard", onClick: (e) => {
                                if (!isPlainClick(e))
                                    return;
                                e.preventDefault();
                                onSelectCollection(c.id);
                            }, children: [_jsxs("div", { className: "vlab-chcard__top", children: [_jsx("span", { className: "vlab-chcard__hash", children: "#" }), _jsx("span", { className: "vlab-chcard__name", children: collectionTitle(c) }), typeof c.blockCount === "number" ? (_jsx("span", { className: "vlab-sidebar__badge", children: c.blockCount })) : undefined] }), c.summary ? _jsx("div", { className: "vlab-chcard__desc", children: c.summary }) : undefined, _jsx("div", { className: "vlab-chcard__meta", children: typeof c.blockCount === "number" ? `${c.blockCount} blocks` : "" })] }, c.id))) }))] }) })] }));
// ---------------------------------------------------------------------------
// Collection view (header + blocks grid)
// ---------------------------------------------------------------------------
const BlockGrid = ({ blocks, collectionId, matchedIds, onOpenBlock }) => (_jsx("div", { className: "vld-grid", children: blocks.map((e) => (_jsx(DataBlockTile, { block: e.block, hidden: matchedIds ? !matchedIds.has(e.block.id) : false, href: collectionHref(collectionId, e.block.id), onOpen: onOpenBlock }, e.block.id))) }));
const VIEW_MODES = ["grid", "table", "canvas", "day"];
const VIEW_LABEL = {
    grid: "Grid",
    table: "Table",
    canvas: "Canvas",
    day: "Day",
};
// The chosen view is mirrored to localStorage so it sticks across collections
// and reloads (the collection view remounts per collection, re-reading this).
const VIEW_KEY = "vlab:data-view";
function loadView() {
    try {
        const v = localStorage.getItem(VIEW_KEY);
        return VIEW_MODES.includes(v ?? "") ? v : "grid";
    }
    catch {
        return "grid";
    }
}
function saveView(view) {
    try {
        localStorage.setItem(VIEW_KEY, view);
    }
    catch {
        // storage disabled / quota exceeded — prototype, so silently drop.
    }
}
// Expanded-block layout: whether the open block renders full-width (and scrolls)
// rather than fit-to-viewport. Persisted so it sticks across blocks and reloads.
const BLOCK_FULL_WIDTH_KEY = "vlab:block-full-width";
function loadBlockFullWidth() {
    try {
        return localStorage.getItem(BLOCK_FULL_WIDTH_KEY) === "1";
    }
    catch {
        return false;
    }
}
function saveBlockFullWidth(on) {
    try {
        localStorage.setItem(BLOCK_FULL_WIDTH_KEY, on ? "1" : "0");
    }
    catch {
        // storage disabled / quota exceeded — prototype, so silently drop.
    }
}
const ViewSwitcher = ({ view, onView, }) => (_jsx("div", { className: "vlab-vs vlab-vs--icons-text", role: "tablist", "aria-label": "View mode", children: VIEW_MODES.map((v) => (_jsx("button", { type: "button", role: "tab", "aria-selected": view === v, title: VIEW_LABEL[v], className: `vlab-vs__btn${view === v ? " is-on" : ""}`, onClick: () => onView(v), children: VIEW_LABEL[v] }, v))) }));
/** One table row — its own component so it can read the block's annotation count. */
const DataTableRow = ({ block, onOpen }) => {
    const annotations = useBlockAnnotationsRead(block.id);
    const [showAnnotations] = useShowAnnotations();
    const label = useBlockDisplayLabel(block);
    const media = blockMediaUrl(block);
    // Quote the URL: media can be an SVG data URI whose literal parens would
    // otherwise make the unquoted `url(...)` an invalid declaration (dropped by
    // the CSSOM, leaving the swatch blank).
    const swatchStyle = media
        ? { backgroundImage: `url("${media}")` }
        : { background: postitTint(block.id).background };
    const added = blockTimestamp(block);
    return (_jsxs("tr", { className: "vlab-table__row", "data-vld-block-id": block.id, onClick: onOpen, children: [_jsx("td", { className: "vlab-table__td vlab-table__td--thumb", children: _jsx("span", { className: "vlab-table__swatch", style: swatchStyle }) }), _jsx("td", { className: "vlab-table__td vlab-table__td--content", children: label }), _jsx("td", { className: "vlab-table__td vlab-table__td--muted", children: block.type }), _jsx("td", { className: "vlab-table__td vlab-table__td--muted", title: formatDate(added), children: relativeTime(added) || formatDate(added) }), showAnnotations ? (_jsx("td", { className: "vlab-table__td vlab-table__td--num", children: annotations.length || "—" })) : undefined] }));
};
const DataTableView = ({ groups, onOpenBlock }) => {
    const [showAnnotations] = useShowAnnotations();
    return (_jsxs("table", { className: "vlab-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "vlab-table__th vlab-table__th--thumb" }), _jsx("th", { className: "vlab-table__th", children: "Content" }), _jsx("th", { className: "vlab-table__th", children: "Type" }), _jsx("th", { className: "vlab-table__th", children: "Added" }), showAnnotations ? (_jsx("th", { className: "vlab-table__th vlab-table__th--num", children: "Comments" })) : undefined] }) }), _jsx("tbody", { children: groups.map((g) => (_jsxs(React.Fragment, { children: [g.label ? (_jsx("tr", { className: "vlab-table__group", children: _jsxs("td", { className: "vlab-table__group-cell", colSpan: showAnnotations ? 5 : 4, children: [g.label, _jsx("span", { className: "vlab-table__group-count", children: g.blocks.length })] }) })) : undefined, g.blocks.map((b) => (_jsx(DataTableRow, { block: b, onOpen: () => onOpenBlock(b.id) }, b.id)))] }, g.key))) })] }));
};
/** Local calendar-day key (YYYY-MM-DD), computed from local parts (no UTC shift). */
function dayKeyOf(ms) {
    const d = new Date(ms);
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}
/**
 * Bucket blocks by the calendar day they were added (`blockTimestamp`), newest
 * day first and newest block first within each day. Blocks with no usable
 * timestamp collect in a single "Undated" group, sorted last.
 */
function dayGroupsFromBlocks(blocks) {
    const UNDATED = "__undated__";
    const buckets = new Map();
    for (const block of blocks) {
        const iso = blockTimestamp(block);
        const ms = iso ? Date.parse(iso) : Number.NaN;
        const dated = !Number.isNaN(ms);
        const key = dated ? dayKeyOf(ms) : UNDATED;
        const bucket = buckets.get(key) ?? { rank: dated ? ms : Number.NEGATIVE_INFINITY, items: [] };
        bucket.items.push({ block, ms: dated ? ms : 0 });
        buckets.set(key, bucket);
    }
    const groups = [];
    for (const [key, bucket] of buckets) {
        bucket.items.sort((a, b) => b.ms - a.ms);
        const dated = key !== UNDATED;
        const d = dated ? new Date(bucket.rank) : undefined;
        groups.push({
            key,
            rank: bucket.rank,
            weekday: d ? d.toLocaleDateString(undefined, { weekday: "short" }) : "",
            date: d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "Undated",
            blocks: bucket.items.map((i) => i.block),
        });
    }
    groups.sort((a, b) => b.rank - a.rank);
    return groups;
}
const DataDayView = ({ blocks, collectionId, onOpenBlock }) => {
    const groups = useMemo(() => dayGroupsFromBlocks(blocks), [blocks]);
    return (_jsx("div", { className: "vld-day", children: groups.map((g) => (_jsxs("section", { className: "vld-day__section", children: [_jsxs("div", { className: "vld-day__label", children: [_jsx("span", { className: "vld-day__weekday", children: g.weekday }), _jsx("span", { className: "vld-day__date", children: g.date })] }), _jsx("div", { className: "vld-day__blocks", children: g.blocks.map((b) => (_jsx(DataBlockTile, { block: b, href: collectionHref(collectionId, b.id), onOpen: onOpenBlock }, b.id))) })] }, g.key))) }));
};
// ---------------------------------------------------------------------------
// Freeform canvas view (live blocks) — drag cards anywhere; click opens them
// ---------------------------------------------------------------------------
const CANVAS_CARD = 200;
const CANVAS_COL_GAP = 28;
// Row pitch leaves room for the caption strip beneath each square card.
const CANVAS_ROW_GAP = 64;
const CANVAS_COLS = 4;
/** Initial grid slot for a card before the user drags it. */
function defaultCanvasPos(index) {
    const col = index % CANVAS_COLS;
    const row = Math.floor(index / CANVAS_COLS);
    return {
        x: col * (CANVAS_CARD + CANVAS_COL_GAP),
        y: row * (CANVAS_CARD + CANVAS_ROW_GAP),
    };
}
// Hand-arranged canvas positions persist per-collection in localStorage so a
// laid-out board survives reloads, view switches, and revisits — mirroring how
// pins and the view mode round-trip above. The store is shaped
// `collectionId → { blockId → {x, y} }`; only dragged cards are stored, so
// un-touched cards keep falling back to their default grid slot.
const CANVAS_POS_KEY = "vlab:canvas-positions";
function loadCanvasPositions(collectionId) {
    try {
        const raw = localStorage.getItem(CANVAS_POS_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object")
            return {};
        const scoped = parsed[collectionId];
        if (!scoped || typeof scoped !== "object")
            return {};
        const out = {};
        for (const [id, val] of Object.entries(scoped)) {
            if (val && typeof val === "object") {
                const { x, y } = val;
                if (typeof x === "number" && typeof y === "number")
                    out[id] = { x, y };
            }
        }
        return out;
    }
    catch {
        return {};
    }
}
function saveCanvasPositions(collectionId, positions) {
    try {
        let store = {};
        const raw = localStorage.getItem(CANVAS_POS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object")
                store = parsed;
        }
        store[collectionId] = positions;
        localStorage.setItem(CANVAS_POS_KEY, JSON.stringify(store));
    }
    catch {
        // storage disabled / quota exceeded — prototype, so silently drop.
    }
}
/** Square card body for a canvas node: contained media, else the post-it/link. */
const CanvasCardBody = ({ block }) => {
    const media = blockMediaUrl(block);
    // Show the scraped website screenshot when "show by default" is on, matching
    // the grid tile so the Canvas view is screenshot-forward too.
    const shot = useBlockShot(block.id);
    const [showShotsByDefault] = useShowShotsByDefault();
    // Pure-link screenshots only — a block with its own media never shows a shot.
    if (shot !== undefined && media === undefined && showShotsByDefault) {
        return (_jsx("div", { className: "vld-media", children: _jsx("img", { src: shot, alt: block.title ?? "Screenshot", loading: "lazy", draggable: false }) }));
    }
    if (media) {
        return (_jsx("div", { className: "vld-media", children: _jsx("img", { src: media, alt: block.title ?? "", loading: "lazy", draggable: false }) }));
    }
    return _jsx(BlockBody, { block: block });
};
const DataCanvasView = ({ blocks, collectionId, onOpenBlock }) => {
    // Per-block position overrides; un-dragged cards fall back to their grid slot.
    // Seeded from (and written back to) localStorage per-collection, so a
    // hand-arranged board survives reloads, view switches, and revisits. The
    // collection view remounts per collection (keyed by id), so reading the store
    // once at mount is enough — `collectionId` is stable for this instance.
    const [overrides, setOverrides] = useState(() => loadCanvasPositions(collectionId));
    // Mirror of the latest overrides, read on drag end so we persist the settled
    // layout once per gesture instead of on every pointer-move tick.
    const overridesRef = useRef(overrides);
    overridesRef.current = overrides;
    const dragRef = useRef(undefined);
    const posOf = (id, index) => overrides[id] ?? defaultCanvasPos(index);
    const onPointerDown = (e, id, index) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        const p = posOf(id, index);
        dragRef.current = { id, dx: e.clientX - p.x, dy: e.clientY - p.y, moved: false };
    };
    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d)
            return;
        d.moved = true;
        setOverrides((prev) => ({ ...prev, [d.id]: { x: e.clientX - d.dx, y: e.clientY - d.dy } }));
    };
    const onPointerUp = (id) => {
        const d = dragRef.current;
        dragRef.current = undefined;
        if (!d)
            return;
        // A press with no movement is a click — open the block. A real drag settled,
        // so persist the new layout (keyed by collection) for next time.
        if (d.moved === false) {
            onOpenBlock(id);
            return;
        }
        saveCanvasPositions(collectionId, overridesRef.current);
    };
    return (_jsx("div", { className: "vlab-canvas vld-canvas", children: blocks.map((b, i) => {
            const p = posOf(b.id, i);
            return (_jsx("div", { className: "vlab-canvas__node vld-canvas-node", "data-vld-block-id": b.id, style: { left: p.x, top: p.y, width: CANVAS_CARD }, onPointerDown: (e) => onPointerDown(e, b.id, i), onPointerMove: onPointerMove, onPointerUp: () => onPointerUp(b.id), children: _jsx(CanvasCardBody, { block: b }) }, b.id));
        }) }));
};
const CollectionView = ({ group, collection, blocks, stacks, count, isLoading, isError, stacksLoading, asking, askError, onAsk, onOpenGroup, onOpenBlock, isPinned, onTogglePin, }) => {
    const [query, setQuery] = useState("");
    const [view, setView] = useState(loadView);
    const chooseView = (v) => {
        setView(v);
        saveView(v);
    };
    const q = query.trim().toLowerCase();
    const directBlocks = blocks ?? [];
    const nonEmptyStacks = (stacks ?? []).filter((s) => s.blocks.length > 0);
    const hasContent = directBlocks.length > 0 || nonEmptyStacks.length > 0;
    // PROTOTYPE: assign each image block a stable, incrementing local number
    // (oldest first, so the newest capture earns the highest). Layout effect so
    // the number is in place before paint — no "Image" → "004" flash. Persisted
    // to localStorage only; see the README's "Temporary local-prototyping hacks".
    const imageIdsChrono = useMemo(() => {
        const imgs = [];
        for (const e of blocks ?? [])
            if (e.block.type === "image")
                imgs.push(e.block);
        for (const s of stacks ?? [])
            for (const e of s.blocks)
                if (e.block.type === "image")
                    imgs.push(e.block);
        imgs.sort((a, b) => (Date.parse(blockTimestamp(a) ?? "") || 0) - (Date.parse(blockTimestamp(b) ?? "") || 0));
        return imgs.map((b) => b.id);
    }, [blocks, stacks]);
    useLayoutEffect(() => {
        ensureImageSeqs(imageIdsChrono);
    }, [imageIdsChrono]);
    // In-collection search narrows the loose blocks and each stack independently;
    // stacks that lose all their blocks to the filter drop out entirely.
    const shownDirect = q ? directBlocks.filter((e) => blockMatchesQuery(e.block, q)) : directBlocks;
    const shownStacks = q
        ? nonEmptyStacks
            .map((s) => ({
            stack: s.stack,
            blocks: s.blocks.filter((e) => blockMatchesQuery(e.block, q)),
        }))
            .filter((s) => s.blocks.length > 0)
        : nonEmptyStacks;
    const matchCount = shownDirect.length + shownStacks.reduce((n, s) => n + s.blocks.length, 0);
    const hasMatches = matchCount > 0;
    // The set of ids the grid should paint. The grid keeps EVERY tile mounted and
    // only toggles visibility against this set (hide-don't-unmount), so a tile's
    // <img> never re-downloads as the query is typed, refined, or cleared. While
    // table/canvas rebuild from the narrowed lists above, the grid renders the
    // full set and leans on this set instead. `undefined` ⇒ no search, show all.
    const matchedIds = useMemo(() => {
        if (!q)
            return undefined;
        const m = new Set();
        for (const e of shownDirect)
            m.add(e.block.id);
        for (const s of shownStacks)
            for (const e of s.blocks)
                m.add(e.block.id);
        return m;
    }, [q, shownDirect, shownStacks]);
    // Flattened blocks (loose + every stack) for the freeform canvas, narrowed by
    // the in-collection search just like the grid and table.
    const canvasBlocks = [
        ...shownDirect.map((e) => e.block),
        ...shownStacks.flatMap((s) => s.blocks.map((e) => e.block)),
    ];
    // Table rows mirror the grid's grouping: loose blocks first (labelled only
    // when stacks follow), then one group per stack.
    const tableGroups = [];
    if (shownDirect.length > 0) {
        tableGroups.push({
            key: "__loose__",
            label: shownStacks.length > 0 ? "Loose blocks" : "",
            blocks: shownDirect.map((e) => e.block),
        });
    }
    for (const s of shownStacks) {
        tableGroups.push({
            key: s.stack.id,
            label: `▤ ${collectionTitle(s.stack)}`,
            blocks: s.blocks.map((e) => e.block),
        });
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "vlab-collhead", children: [_jsxs("div", { className: "vlab-collhead__top", children: [_jsxs("div", { className: "vlab-crumb", children: [group ? (_jsxs(_Fragment, { children: [_jsx("a", { href: groupHref(group.id), className: "vlab-crumb__seg vlab-crumb__link", onClick: (e) => {
                                                    if (!isPlainClick(e))
                                                        return;
                                                    e.preventDefault();
                                                    onOpenGroup(group.id);
                                                }, children: groupTitle(group) }), _jsx("span", { className: "vlab-crumb__sep", children: "/" })] })) : undefined, _jsxs("span", { className: "vlab-crumb__seg vlab-crumb__seg--active", children: ["# ", collectionTitle(collection)] }), _jsx("button", { type: "button", className: `vld-crumb-star${isPinned ? " is-pinned" : ""}`, onClick: () => onTogglePin(collection.id), title: isPinned ? "Remove from favorites" : "Add to favorites", "aria-label": isPinned ? "Remove from favorites" : "Add to favorites", "aria-pressed": isPinned, children: _jsx(StarIcon, {}) })] }), hasContent ? (_jsxs("div", { className: "vld-coll-actions", children: [_jsx(ViewSwitcher, { view: view, onView: chooseView }), _jsx("div", { className: "vld-coll-search", children: _jsx("input", { type: "search", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Search this collection\u2026", "aria-label": "Search this collection" }) })] })) : undefined] }), _jsxs("div", { className: "vlab-collhead__stats", children: [q ? (_jsxs("span", { children: [_jsx("span", { className: "vlab-collhead__stat-k", children: matchCount }), " ", matchCount === 1 ? "match" : "matches"] })) : (_jsxs("span", { children: [_jsx("span", { className: "vlab-collhead__stat-k", children: count ?? collection.blockCount ?? 0 }), " ", "blocks"] })), nonEmptyStacks.length > 0 ? (_jsxs("span", { children: [_jsx("span", { className: "vlab-collhead__stat-k", children: nonEmptyStacks.length }), " stacks"] })) : undefined] })] }), _jsx("div", { className: "vlab-panel", children: isLoading ? (_jsx("div", { className: "vlab-empty", children: "Loading blocks\u2026" })) : isError ? (_jsx("div", { className: "vlab-empty", children: "Couldn't load this collection." })) : !hasContent ? (_jsx("div", { className: "vlab-empty", children: stacksLoading ? "Loading blocks…" : "No blocks in this collection yet." })) : view === "canvas" ? (!hasMatches ? (_jsxs("div", { className: "vlab-empty", children: ["No blocks match \u201C", query.trim(), "\u201D."] })) : (_jsx(DataCanvasView, { blocks: canvasBlocks, collectionId: collection.id, onOpenBlock: onOpenBlock }))) : view === "table" ? (!hasMatches ? (_jsxs("div", { className: "vlab-empty", children: ["No blocks match \u201C", query.trim(), "\u201D."] })) : (_jsx(DataTableView, { groups: tableGroups, onOpenBlock: onOpenBlock }))) : view === "day" ? (!hasMatches ? (_jsxs("div", { className: "vlab-empty", children: ["No blocks match \u201C", query.trim(), "\u201D."] })) : (_jsx(DataDayView, { blocks: canvasBlocks, collectionId: collection.id, onOpenBlock: onOpenBlock }))) : (
                // Grid view renders the FULL block set and hides non-matches via
                // `matchedIds`, so tiles (and their images) are never unmounted by the
                // search. Whole groups collapse with `display:none` once a query
                // empties them; group heads gate on `nonEmptyStacks` (not the narrowed
                // list) so they don't appear/disappear as the query changes.
                _jsxs(_Fragment, { children: [!hasMatches ? (_jsxs("div", { className: "vlab-empty", children: ["No blocks match \u201C", query.trim(), "\u201D."] })) : undefined, directBlocks.length > 0 ? (_jsxs("div", { className: "vlab-group", style: q && shownDirect.length === 0 ? { display: "none" } : undefined, children: [nonEmptyStacks.length > 0 ? (_jsxs("div", { className: "vlab-group__head", children: [_jsx("span", { className: "vlab-group__label", children: "Loose blocks" }), _jsx("span", { className: "vlab-group__count", children: shownDirect.length })] })) : undefined, _jsx(BlockGrid, { blocks: directBlocks, collectionId: collection.id, matchedIds: matchedIds, onOpenBlock: onOpenBlock })] })) : undefined, nonEmptyStacks.map((s) => {
                            const shownCount = q
                                ? s.blocks.reduce((n, e) => n + (matchedIds?.has(e.block.id) ? 1 : 0), 0)
                                : s.blocks.length;
                            return (_jsxs("div", { className: "vlab-group", style: q && shownCount === 0 ? { display: "none" } : undefined, children: [_jsxs("div", { className: "vlab-group__head", children: [_jsxs("span", { className: "vlab-group__label", children: ["\u25A4 ", collectionTitle(s.stack)] }), _jsx("span", { className: "vlab-group__count", children: shownCount })] }), _jsx(BlockGrid, { blocks: s.blocks, collectionId: collection.id, matchedIds: matchedIds, onOpenBlock: onOpenBlock })] }, s.stack.id));
                        })] })) })] }));
};
function scrapeStatusText(state) {
    switch (state.type) {
        case "empty":
            return "No URLs to scrape in this collection.";
        case "scraping":
            return `Scraping… ${state.done}/${state.total}`;
        case "done": {
            const { requested, failed } = state.result;
            return failed > 0
                ? `Scraped ${requested} · ${failed} failed`
                : `Scraped ${requested} screenshot${requested === 1 ? "" : "s"}.`;
        }
        default:
            return undefined;
    }
}
const DebugDock = ({ targets }) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("debug");
    const [state, setState] = useState({ type: "idle" });
    const [showShotsByDefault, setShowShotsByDefault] = useShowShotsByDefault();
    const [showAnnotations, setShowAnnotations] = useShowAnnotations();
    const shotCount = useShotCount();
    const scraping = state.type === "scraping";
    const runScrape = async () => {
        if (scraping)
            return;
        if (targets.length === 0) {
            setState({ type: "empty" });
            return;
        }
        setState({ type: "scraping", done: 0, total: targets.length });
        const result = await scrapeScreenshots(targets, ({ done, total }) => setState({ type: "scraping", done, total }));
        setState({ type: "done", result });
    };
    const statusText = scrapeStatusText(state);
    return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: `vld-debug-btn${open ? " is-open" : ""}`, onClick: () => setOpen((o) => !o), title: "Debug tools", "aria-label": "Debug tools", children: "\uD83D\uDC1E" }), open ? (_jsxs("div", { className: "vld-debug-modal", role: "dialog", "aria-label": "Debug tools", children: [_jsxs("div", { className: "vld-debug-modal__head", children: [_jsxs("div", { className: "vld-debug-modal__tabs", role: "tablist", "aria-label": "Debug panel sections", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": tab === "debug", className: `vld-debug-modal__tab${tab === "debug" ? " is-active" : ""}`, onClick: () => setTab("debug"), children: "Debug" }), _jsx("button", { type: "button", role: "tab", "aria-selected": tab === "about", className: `vld-debug-modal__tab${tab === "about" ? " is-active" : ""}`, onClick: () => setTab("about"), children: "About" }), _jsx("button", { type: "button", role: "tab", "aria-selected": tab === "usecases", className: `vld-debug-modal__tab${tab === "usecases" ? " is-active" : ""}`, onClick: () => setTab("usecases"), children: "Use Cases" })] }), _jsx("button", { type: "button", className: "vlab-x", onClick: () => setOpen(false), "aria-label": "Close debug tools", children: "\u00D7" })] }), tab === "debug" ? (_jsxs("div", { className: "vld-debug-modal__body", children: [_jsx("button", { type: "button", className: "vlab-btn vld-debug-modal__action", onClick: () => void runScrape(), disabled: scraping, children: scraping ? "Scraping…" : "Scrape screenshots for current collection" }), _jsxs("label", { className: "vld-debug-modal__toggle", children: [_jsx("input", { type: "checkbox", checked: !showAnnotations, onChange: (e) => setShowAnnotations(!e.target.checked) }), "Hide annotations sidebar"] }), _jsxs("label", { className: "vld-debug-modal__toggle", children: [_jsx("input", { type: "checkbox", checked: showShotsByDefault, onChange: (e) => setShowShotsByDefault(e.target.checked) }), "Show screenshot by default"] }), _jsxs("div", { className: "vld-debug-modal__status", children: [statusText ? _jsx("div", { children: statusText }) : undefined, _jsxs("div", { className: "vld-debug-modal__hint", children: [shotCount, " stored \u00B7 hover a block to preview"] })] })] })) : tab === "about" ? (_jsxs("div", { className: "vld-debug-modal__body", children: [_jsxs("p", { className: "vld-debug-modal__intro", children: ["Stash is a visual research tool. It helps you keep your references organized, easy to search. It\u2019s extremely easy to add and retrieve from here. A mix of Google Drive x FigJam x Are.na.", _jsx("br", {}), "It's fast. Speed is the point."] }), _jsxs("div", { className: "vld-debug-modal__demo", children: [_jsx("div", { className: "vld-debug-modal__demo-title", children: "To demo" }), _jsx("ul", { className: "vld-debug-modal__demo-list", children: _jsxs("li", { children: [_jsx("kbd", { className: "vld-debug-modal__kbd", children: "\u2318G" }), " speed"] }) })] })] })) : (_jsx("div", { className: "vld-debug-modal__body", children: _jsxs("div", { className: "vld-debug-modal__demo", children: [_jsx("div", { className: "vld-debug-modal__demo-title", children: "Use cases" }), _jsxs("ul", { className: "vld-debug-modal__demo-list", children: [_jsx("li", { children: "Brainstorming \u201CListers\u201D" }), _jsx("li", { children: "Tracking Quartz progress" }), _jsx("li", { children: "Tracking Neal interview" }), _jsx("li", { children: "Working on Loaf projects" })] })] }) }))] })) : undefined] }));
};
// ---------------------------------------------------------------------------
// Block inspector + ask bar (hover a block, press Space to ask)
// ---------------------------------------------------------------------------
//
// A lightweight "inspect" gesture: while pointing at any block (grid tile,
// table row, or canvas node — every one carries a `data-vld-block-id`), pressing
// Space locks onto it. We draw a 4px outline that tracks the block's on-screen
// box and float an ask bar at the bottom of the screen. Questions are answered
// by `chatWithCollection` scoped to just that one block, so the reply is grounded
// in the block's own content (and its scraped article/artifact text).
/** Question-length cap so a runaway paste can't be sent to the chat endpoint. */
const MAX_ASK_LENGTH = 1000;
/** Newest exchanges kept in the in-memory transcript (older turns drop off). */
const MAX_ASK_TURNS = 12;
/** Grow the ask input up to this height, then scroll. */
const ASK_INPUT_MAX_PX = 120;
/**
 * A fixed 4px outline that hugs the locked block's on-screen box. The block
 * lives in normal flow (and may reflow as images load or the user scrolls), so
 * we re-measure its rect every animation frame and only re-render when it moves.
 */
const BlockInspectorOverlay = ({ blockId }) => {
    const [rect, setRect] = useState(undefined);
    useEffect(() => {
        let raf = 0;
        let prevKey = "";
        const tick = () => {
            const el = document.querySelector(`[data-vld-block-id="${CSS.escape(blockId)}"]`);
            if (el) {
                const r = el.getBoundingClientRect();
                const key = `${r.left}|${r.top}|${r.width}|${r.height}`;
                if (key !== prevKey) {
                    prevKey = key;
                    setRect(r);
                }
            }
            else if (prevKey !== "") {
                prevKey = "";
                setRect(undefined);
            }
            raf = requestAnimationFrame(tick);
        };
        tick();
        return () => cancelAnimationFrame(raf);
    }, [blockId]);
    if (!rect)
        return undefined;
    return (_jsx("div", { className: "vld-inspect-outline", style: { left: rect.left, top: rect.top, width: rect.width, height: rect.height }, "aria-hidden": "true" }));
};
/**
 * Bottom-docked ask bar for the locked block. Holds a tiny in-memory transcript
 * and replays it as `history` on each turn so follow-ups stay in context. Keyed
 * by block id at the call site, so switching blocks resets the conversation.
 */
const AskBar = ({ block, onClose }) => {
    const [input, setInput] = useState("");
    const [turns, setTurns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const inputRef = useRef(null);
    const threadRef = useRef(null);
    const caption = blockCaption(block);
    // Focus the input the moment the bar opens (or the locked block changes).
    useEffect(() => {
        inputRef.current?.focus();
    }, []);
    // Keep the transcript pinned to the latest turn.
    useEffect(() => {
        const el = threadRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [turns, loading]);
    const grow = (el) => {
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, ASK_INPUT_MAX_PX)}px`;
    };
    const ask = async () => {
        const message = input.trim();
        if (message.length === 0 || loading)
            return;
        const history = turns;
        setTurns((prev) => [...prev, { role: "user", content: message }].slice(-MAX_ASK_TURNS));
        setInput("");
        if (inputRef.current)
            inputRef.current.style.height = "auto";
        setLoading(true);
        setError(undefined);
        try {
            const res = await chatWithCollection({
                blockIds: [block.id],
                title: caption,
                message,
                history,
            });
            setTurns((prev) => [...prev, { role: "assistant", content: res.answer }].slice(-MAX_ASK_TURNS));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't get an answer.");
        }
        finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };
    const hasThread = turns.length > 0 || loading || error !== undefined;
    return (_jsx("div", { className: "vld-ask", role: "dialog", "aria-label": "Ask about this block", children: _jsxs("div", { className: "vld-ask__inner", children: [_jsxs("div", { className: "vld-ask__head", children: [_jsxs("span", { className: "vld-ask__target", children: [_jsx("span", { className: "vld-ask__target-dot", "aria-hidden": "true" }), _jsx("span", { className: "vld-ask__target-name", title: caption, children: caption }), _jsx("span", { className: "vld-ask__target-type", children: block.type })] }), _jsx("span", { className: "vld-ask__hint", children: "Esc to close" }), _jsx("button", { type: "button", className: "vlab-x vld-ask__close", onClick: onClose, "aria-label": "Close ask bar", children: "\u00D7" })] }), hasThread ? (_jsxs("div", { className: "vld-ask__thread", ref: threadRef, children: [turns.map((t, i) => (_jsx("div", { className: `vld-ask__turn vld-ask__turn--${t.role}`, children: t.content }, i))), loading ? (_jsx("div", { className: "vld-ask__turn vld-ask__turn--assistant vld-ask__turn--loading", children: "Thinking\u2026" })) : undefined, error !== undefined ? _jsx("div", { className: "vld-ask__error", children: error }) : undefined] })) : undefined, _jsxs("form", { className: "vld-ask__form", onSubmit: (e) => {
                        e.preventDefault();
                        void ask();
                    }, children: [_jsx("textarea", { ref: inputRef, className: "vld-ask__input", value: input, maxLength: MAX_ASK_LENGTH, rows: 1, placeholder: "Ask about this block\u2026 e.g. How did they do their interview format in here?", onChange: (e) => {
                                setInput(e.target.value);
                                grow(e.currentTarget);
                            }, onKeyDown: (e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    void ask();
                                }
                            } }), _jsx("button", { type: "submit", className: "vlab-btn vld-ask__send", disabled: loading || input.trim().length === 0, children: loading ? "…" : "Ask" })] })] }) }));
};
// ---------------------------------------------------------------------------
// Latest Activity feed (recent comments + recent blocks)
// ---------------------------------------------------------------------------
/** How many recent blocks to pull, how many comments to resolve, feed cap. */
const ACTIVITY_BLOCK_LIMIT = 40;
const ACTIVITY_COMMENT_LIMIT = 30;
const ACTIVITY_FEED_LIMIT = 60;
/** A short description of what a comment carries when it has no written text. */
function commentPreview(a) {
    const text = a.text?.trim();
    if (text)
        return text;
    if (annotationPreviewImages(a).length > 0)
        return "Added an image";
    if (a.kind === "voice")
        return "Recorded a voice note";
    if (a.kind === "link")
        return a.linkLabel ? `Linked → ${a.linkLabel}` : "Linked a block";
    return "Comment";
}
/** A feed row's leading square: the block's media, else a tinted type monogram. */
const FeedThumb = ({ block }) => {
    const media = block ? blockMediaUrl(block) : undefined;
    if (media) {
        return (_jsx("span", { className: "vld-feed__thumb", children: _jsx("img", { src: media, alt: "", loading: "lazy", draggable: false }) }));
    }
    if (block) {
        return (_jsx("span", { className: "vld-feed__thumb vld-feed__thumb--mono", style: postitTint(block.id), children: block.type.slice(0, 3).toUpperCase() }));
    }
    return _jsx("span", { className: "vld-feed__thumb vld-feed__thumb--mono", children: "\uD83D\uDCAC" });
};
const FeedRow = ({ item, authorName, collectionName, onOpen, onMarkMentionRead }) => {
    const block = item.block;
    const collectionId = block?.collectionId;
    const blockId = block?.id;
    const canOpen = collectionId !== undefined && blockId !== undefined;
    const verb = item.kind === "block" ? "New block" : item.kind === "comment" ? "New comment" : "Mention";
    const primary = item.kind === "block"
        ? blockCaption(item.block)
        : item.kind === "comment"
            ? commentPreview(item.annotation)
            : "Tagged you in a comment";
    // Both comments and mentions hang off a block — name it when resolved.
    const target = item.kind !== "block" && item.block ? blockCaption(item.block) : undefined;
    const place = collectionId ? collectionName(collectionId) : undefined;
    const unread = item.kind === "mention" && item.mention.readAt === undefined;
    // Opening a mention also clears its unread state.
    const markReadOnOpen = () => {
        if (item.kind === "mention" && unread)
            onMarkMentionRead(item.mention.id);
    };
    const body = (_jsxs(_Fragment, { children: [_jsx(FeedThumb, { block: block }), _jsxs("div", { className: "vld-feed__body", children: [_jsxs("div", { className: "vld-feed__head", children: [_jsx("span", { className: `vld-feed__chip vld-feed__chip--${item.kind}`, children: verb }), item.kind === "comment" ? (_jsx("span", { className: "vld-feed__author", children: authorName })) : undefined, unread ? _jsx("span", { className: "vld-feed__unread", "aria-label": "Unread" }) : undefined, _jsx("span", { className: "vld-feed__time", children: relativeTimeMs(item.at) })] }), _jsx("div", { className: "vld-feed__primary", children: primary }), target || place ? (_jsxs("div", { className: "vld-feed__meta", children: [target ? _jsxs("span", { children: ["on ", target] }) : undefined, place ? _jsxs("span", { children: ["in ", place] }) : undefined] })) : undefined] })] }));
    if (!canOpen)
        return _jsx("div", { className: "vld-feed__row vld-feed__row--static", children: body });
    return (_jsx("a", { className: "vld-feed__row", href: collectionHref(collectionId, blockId), onClick: (e) => {
            if (!isPlainClick(e))
                return;
            e.preventDefault();
            markReadOnOpen();
            onOpen(collectionId, blockId);
        }, children: body }));
};
/**
 * The "Latest Activity" panel: recent new blocks (across every collection) and
 * recent new comments, merged into one newest-first feed. Blocks come from the
 * `/block/list` endpoint; comments are the local annotation prototype, whose
 * blocks are resolved on demand so each row can show — and open — its block.
 */
const ActivityFeedView = ({ collectionName, onOpenBlock }) => {
    const { account } = useAccountMe();
    const authorName = account?.username ? `@${account.username}` : "You";
    const queryClient = useQueryClient();
    const blocksQ = useQuery({
        queryKey: ["vlab-data", "activity-blocks"],
        queryFn: () => listBlocks({ orderBy: "createdAt", order: "desc", limit: ACTIVITY_BLOCK_LIMIT }),
        staleTime: 30_000,
    });
    // Mentions of the current user (someone tagged them in a comment). Failures
    // — e.g. before the mention backend deploys — degrade to an empty slice
    // rather than breaking the rest of the feed.
    const mentionsQ = useQuery({
        queryKey: ["vlab-data", "activity-mentions"],
        queryFn: () => listMentions("all", ACTIVITY_COMMENT_LIMIT),
        staleTime: 30_000,
        retry: false,
    });
    const mentions = useMemo(() => mentionsQ.data ?? [], [mentionsQ.data]);
    const markMentionReadInFeed = (id) => {
        void markMentionRead(id).then(() => queryClient.invalidateQueries({ queryKey: ["vlab-data", "activity-mentions"] }));
    };
    // Every local comment, newest first, capped to what the feed will resolve.
    const allComments = useAllAnnotations();
    const recentComments = useMemo(() => [...allComments]
        .sort((a, b) => b.annotation.createdAt - a.annotation.createdAt)
        .slice(0, ACTIVITY_COMMENT_LIMIT), [allComments]);
    // The blocks comments + mentions live on. Sorted ids keep the key stable.
    const feedBlockIds = useMemo(() => {
        const ids = new Set();
        for (const c of recentComments)
            ids.add(c.blockId);
        for (const m of mentions)
            ids.add(m.blockId);
        return [...ids].sort();
    }, [recentComments, mentions]);
    const feedBlocksQ = useQuery({
        queryKey: ["vlab-data", "activity-feed-blocks", feedBlockIds],
        enabled: feedBlockIds.length > 0,
        staleTime: 30_000,
        queryFn: async () => {
            const envelopes = await Promise.all(feedBlockIds.map((id) => fetchBlockEnvelope(id)));
            const map = new Map();
            for (const env of envelopes)
                if (env)
                    map.set(env.block.id, env.block);
            return map;
        },
    });
    const items = useMemo(() => {
        const out = [];
        for (const env of blocksQ.data?.blocks ?? []) {
            const at = Date.parse(env.block.createdAt);
            out.push({
                key: `block:${env.block.id}`,
                at: Number.isNaN(at) ? 0 : at,
                kind: "block",
                block: env.block,
            });
        }
        const blockMap = feedBlocksQ.data;
        for (const c of recentComments) {
            out.push({
                key: `comment:${c.annotation.id}`,
                at: c.annotation.createdAt,
                kind: "comment",
                annotation: c.annotation,
                block: blockMap?.get(c.blockId),
            });
        }
        for (const m of mentions) {
            const at = Date.parse(m.createdAt);
            out.push({
                key: `mention:${m.id}`,
                at: Number.isNaN(at) ? 0 : at,
                kind: "mention",
                mention: m,
                block: blockMap?.get(m.blockId),
            });
        }
        out.sort((a, b) => b.at - a.at);
        return out.slice(0, ACTIVITY_FEED_LIMIT);
    }, [blocksQ.data, recentComments, mentions, feedBlocksQ.data]);
    const loading = blocksQ.isLoading && recentComments.length === 0;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "vlab-collhead", children: [_jsx("div", { className: "vlab-collhead__top", children: _jsx("div", { className: "vlab-crumb", children: _jsx("span", { className: "vlab-crumb__seg vlab-crumb__seg--active", children: "Latest Activity" }) }) }), _jsx("div", { className: "vlab-collhead__stats", children: _jsxs("span", { children: [_jsx("span", { className: "vlab-collhead__stat-k", children: items.length }), " ", items.length === 1 ? "update" : "updates"] }) })] }), _jsx("div", { className: "vlab-panel", children: loading ? (_jsx("div", { className: "vlab-empty", children: "Loading activity\u2026" })) : items.length === 0 ? (_jsx("div", { className: "vlab-empty", children: "No recent activity yet." })) : (_jsx("div", { className: "vld-feed", children: items.map((item) => (_jsx(FeedRow, { item: item, authorName: authorName, collectionName: collectionName, onOpen: onOpenBlock, onMarkMentionRead: markMentionReadInFeed }, item.key))) })) })] }));
};
// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
export const HomeScreen = () => {
    const queryClient = useQueryClient();
    const groupsQ = useQuery({
        queryKey: ["vlab-data", "groups"],
        queryFn: listGroups,
        staleTime: LIST_STALE_MS,
    });
    const collectionsQ = useQuery({
        queryKey: ["vlab-data", "collections"],
        queryFn: () => listCollections({ layerIndex: 1 }),
        staleTime: LIST_STALE_MS,
    });
    // Build projects: each group with its child collections, plus a synthetic
    // "Ungrouped" bucket for orphans. Everything is ordered reverse-chrono by
    // most-recent activity (newest first), at both the group and channel level.
    const projects = useMemo(() => {
        const groups = groupsQ.data?.collections ?? [];
        const cols = collectionsQ.data?.collections ?? [];
        const groupIds = new Set(groups.map((g) => g.id));
        const byParent = new Map();
        const orphans = [];
        for (const c of cols) {
            const pid = c.parentCollectionId;
            if (pid && groupIds.has(pid)) {
                const arr = byParent.get(pid);
                if (arr)
                    arr.push(c);
                else
                    byParent.set(pid, [c]);
            }
            else {
                orphans.push(c);
            }
        }
        const byRecency = (a, b) => recencyMs(b) - recencyMs(a);
        const entries = groups.map((g) => ({
            group: g,
            channels: (byParent.get(g.id) ?? []).slice().sort(byRecency),
        }));
        if (orphans.length > 0) {
            entries.push({
                group: {
                    id: UNGROUPED_ID,
                    ownerId: "",
                    type: "private",
                    title: "Ungrouped",
                    metadata: {},
                    createdAt: "",
                },
                channels: orphans.slice().sort(byRecency),
            });
        }
        entries.sort((a, b) => entryRecencyMs(b) - entryRecencyMs(a));
        return entries;
    }, [groupsQ.data, collectionsQ.data]);
    const [selection, setSelection] = useState(() => {
        const url = readUrlSelection();
        if (url?.kind === "collection")
            return { kind: "collection", id: url.id };
        if (url?.kind === "group")
            return { kind: "group", id: url.id };
        if (url?.kind === "activity")
            return { kind: "activity" };
        return undefined;
    });
    // A `?c=<slug>` URL arrives unresolved — its id is recovered from the loaded
    // collection list (see the resolve effect below). Until then `selection`
    // stays undefined and the default-selection / URL-write effects hold off.
    const [pendingSlug, setPendingSlug] = useState(() => {
        const url = readUrlSelection();
        return url?.kind === "collection-slug" ? url.slug : undefined;
    });
    // The index/search modal — opened by the sidebar search box and ⌘G. It holds
    // both the A–Z collection index and the fast block search behind a toggle.
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchScope, setSearchScope] = useState("collections");
    // The "New channel" popup (sidebar button above Latest Activity).
    const [newChannelOpen, setNewChannelOpen] = useState(false);
    // A `?b=` block deep-link opens its modal once the collection's blocks load.
    const [activeBlockId, setActiveBlockId] = useState(readBlockParam);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [pinnedIds, setPinnedIds] = useState(loadPinnedIds);
    // Query canvas: the temporary board materialized from an "Ask" + the
    // assistant's relevant-block picks. `asking` gates the ask box while the
    // chat round-trips; `activeCanvasId` opens the overlay over everything.
    const [activeCanvasId, setActiveCanvasId] = useState(undefined);
    const [asking, setAsking] = useState(false);
    const [askError, setAskError] = useState(undefined);
    // Inspect/ask gesture: the block currently locked for Q&A (Space over a block),
    // plus a ref to whatever block the pointer is over right now (read at the
    // moment Space is pressed, so hover tracking never re-renders the grid).
    const [inspectBlockId, setInspectBlockId] = useState(undefined);
    const hoveredBlockIdRef = useRef(undefined);
    // Slug ⇄ id maps over the loaded collection list, used to (a) resolve a
    // `?c=<slug>` URL to its id and (b) render the pretty slug back into the URL.
    // Ambiguous slugs (the same handle under two parents) are dropped so the URL
    // we emit always round-trips to exactly one collection — collisions fall back
    // to the raw id rather than risk opening the wrong collection.
    const slugToId = useMemo(() => {
        const cols = collectionsQ.data?.collections ?? [];
        const counts = new Map();
        const map = new Map();
        for (const c of cols) {
            if (!c.slug)
                continue;
            // A slug that collides with a reserved first segment (`/g`, `/activity`,
            // `/dev`) would be misread as that route — fall back to the raw id.
            if (RESERVED_FIRST_SEGMENTS.has(c.slug))
                continue;
            counts.set(c.slug, (counts.get(c.slug) ?? 0) + 1);
            map.set(c.slug, c.id);
        }
        for (const [slug, n] of counts)
            if (n > 1)
                map.delete(slug);
        return map;
    }, [collectionsQ.data]);
    const idToSlug = useMemo(() => {
        const inv = new Map();
        for (const [slug, id] of slugToId)
            inv.set(id, slug);
        return inv;
    }, [slugToId]);
    // The `popstate` listener is registered once but needs the latest slug map to
    // resolve a popped `/<slug>` URL without a flicker; read it through a ref.
    const slugToIdRef = useRef(slugToId);
    slugToIdRef.current = slugToId;
    // Back/Forward: re-derive the view from the URL the browser just restored.
    // This is programmatic, so the mirror effect below only replaceState's the
    // same path — no new history entry is created by navigating the history.
    useEffect(() => {
        const onPopState = () => {
            const url = readUrlSelection();
            const blockId = readBlockParam();
            if (url?.kind === "collection") {
                setSelection({ kind: "collection", id: url.id });
                setPendingSlug(undefined);
            }
            else if (url?.kind === "collection-slug") {
                const id = slugToIdRef.current.get(url.slug);
                if (id) {
                    setSelection({ kind: "collection", id });
                    setPendingSlug(undefined);
                }
                else {
                    // List not loaded yet — defer to the slug-resolve effect.
                    setPendingSlug(url.slug);
                }
            }
            else if (url?.kind === "group") {
                setSelection({ kind: "group", id: url.id });
                setPendingSlug(undefined);
            }
            else if (url?.kind === "activity") {
                setSelection({ kind: "activity" });
                setPendingSlug(undefined);
            }
            else {
                setSelection(undefined);
                setPendingSlug(undefined);
            }
            setActiveBlockId(blockId);
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);
    // Resolve a `?c=<slug>` URL once the collection list is available. A hit sets
    // the selection by id; a miss (stale / unknown slug) just clears the pending
    // marker so the default-selection effect can take over.
    useEffect(() => {
        if (pendingSlug === undefined)
            return;
        if (collectionsQ.isLoading || groupsQ.isLoading)
            return;
        const id = slugToId.get(pendingSlug);
        if (id)
            setSelection({ kind: "collection", id });
        setPendingSlug(undefined);
    }, [pendingSlug, slugToId, collectionsQ.isLoading, groupsQ.isLoading]);
    // Once data arrives, settle on a valid view.
    //
    // A collection named in the URL is ALWAYS trusted, even when it isn't in the
    // preloaded tree (a stack, a collection reached only by direct id, anything
    // `/collection/list` doesn't surface): its `/details` fetch renders it
    // standalone, so we only expand its parent group when we happen to know it
    // and otherwise leave the selection untouched. A group named in the URL is
    // kept when it exists. Only a fresh visit with no selection — or a stale
    // group id — falls back to the most-recent group.
    useEffect(() => {
        if (pendingSlug !== undefined)
            return; // wait for slug → id resolution
        // A collection is always trusted (its `/details` fetch renders it even when
        // absent from the tree); activity is a deliberate view. Both stand as-is.
        if (selection?.kind === "collection" || selection?.kind === "activity")
            return;
        if (selection?.kind === "group") {
            // Keep a valid group; only fall back once we know the id is stale.
            if (projects.length === 0)
                return;
            if (projects.some((p) => p.group.id === selection.id))
                return;
        }
        // Fresh visit (nothing selected) or a stale group id → land on Latest Activity.
        setSelection({ kind: "activity" });
    }, [projects, selection, pendingSlug]);
    // Mirror the current view into the URL path (replaceState — no history spam)
    // so a reload or shared link reopens the same group / collection. Collections
    // are written with their pretty `slug` when one round-trips (else the raw id).
    // While a `/<slug>` URL is still resolving we hold off so we don't clobber it
    // with an empty/stale value.
    useEffect(() => {
        if (pendingSlug !== undefined)
            return;
        const collectionValue = selection?.kind === "collection" ? idToSlug.get(selection.id) : undefined;
        writeSelectionPath(selection, collectionValue, activeBlockId);
    }, [selection, pendingSlug, idToSlug, activeBlockId]);
    // Push a fresh history entry for an explicit user navigation (a click). The
    // mirror effect above then canonicalizes the URL (e.g. raw id → pretty slug)
    // in place. Guarded so re-selecting the current view doesn't stack dupes.
    const pushView = (next, blockId) => {
        if (typeof window === "undefined")
            return;
        const collectionValue = next?.kind === "collection" ? idToSlug.get(next.id) : undefined;
        const path = selectionPath(next, collectionValue, blockId);
        if (path === window.location.pathname)
            return;
        window.history.pushState(blockId ? { puttyBlock: true } : undefined, "", path);
    };
    // Close the open block. If we opened it by pushing an entry, pop it so Back /
    // Forward stay coherent (and the URL returns to the collection); a deep-linked
    // block has no such entry, so just clear it and let the mirror effect tidy up.
    // Stable identity (no deps) so the Esc-key effect doesn't re-bind every render.
    const closeBlock = useCallback(() => {
        if (typeof window !== "undefined" && window.history.state?.puttyBlock) {
            window.history.back();
        }
        else {
            setActiveBlockId(undefined);
        }
    }, []);
    const selectGroup = (id) => {
        pushView({ kind: "group", id });
        setSelection({ kind: "group", id });
        setActiveBlockId(undefined);
    };
    const selectCollection = (id) => {
        pushView({ kind: "collection", id });
        setSelection({ kind: "collection", id });
        setActiveBlockId(undefined);
    };
    const selectActivity = () => {
        pushView({ kind: "activity" });
        setSelection({ kind: "activity" });
        setActiveBlockId(undefined);
    };
    // Opening a block's modal supersedes the inspect/ask gesture — drop the lock
    // so the ask bar doesn't linger behind the modal. The block is pushed onto the
    // current collection's path so Back closes the modal.
    const openBlock = (id) => {
        pushView(selection, id);
        setInspectBlockId(undefined);
        setActiveBlockId(id);
    };
    // Jump from the activity feed to a block: switch to its collection (so the
    // grid + `/details` load it) and open its modal. The collection's blocks load
    // via `detailsQ`, after which the modal resolves the id from `allBlocks` —
    // the same path a `/<collection>/<blk_…>` deep link takes.
    const openBlockInCollection = (collectionId, blockId) => {
        pushView({ kind: "collection", id: collectionId }, blockId);
        setInspectBlockId(undefined);
        setSelection({ kind: "collection", id: collectionId });
        setActiveBlockId(blockId);
    };
    // Open the index/search modal in a given scope (sidebar search box + ⌘G).
    const openSearch = (scope = "collections") => {
        setSearchScope(scope);
        setSearchOpen(true);
    };
    // Optimistic capture: drop a freshly-captured block straight into its
    // collection's `/details` cache so it shows in the grid immediately, ahead of
    // the async pipeline. It reconciles when `/details` next refetches (same id).
    const insertOptimisticBlock = (envelope) => {
        const key = ["vlab-data", "details", envelope.block.collectionId];
        queryClient.setQueryData(key, (prev) => prev
            ? { ...prev, blocks: [envelope, ...prev.blocks], count: (prev.count ?? prev.blocks.length) + 1 }
            : prev);
    };
    const removeOptimisticBlock = (id, collectionId) => {
        const key = ["vlab-data", "details", collectionId];
        queryClient.setQueryData(key, (prev) => prev
            ? {
                ...prev,
                blocks: prev.blocks.filter((e) => e.block.id !== id),
                count: Math.max(0, (prev.count ?? prev.blocks.length) - 1),
            }
            : prev);
    };
    const togglePin = (id) => setPinnedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        savePinnedIds(next);
        return next;
    });
    // Real groups a new channel can be filed under (the synthetic "Ungrouped"
    // bucket isn't a real collection, so it can't parent a child).
    const newChannelGroups = useMemo(() => projects
        .filter((p) => p.group.id !== UNGROUPED_ID)
        .map((p) => ({ id: p.group.id, title: groupTitle(p.group) })), [projects]);
    // Default the modal's group to the one in view: the selected group, the
    // selected collection's parent, else the most-recent group.
    const newChannelDefaultGroupId = useMemo(() => {
        if (selection?.kind === "group")
            return selection.id;
        if (selection?.kind === "collection") {
            const parent = projects.find((p) => p.channels.some((c) => c.id === selection.id));
            if (parent && parent.group.id !== UNGROUPED_ID)
                return parent.group.id;
        }
        return newChannelGroups[0]?.id;
    }, [selection, projects, newChannelGroups]);
    // A freshly created channel: refresh the lists so it appears in the tree and
    // index, then open it.
    const handleChannelCreated = (collection) => {
        void queryClient.invalidateQueries({ queryKey: ["vlab-data", "collections"] });
        void queryClient.invalidateQueries({ queryKey: ["vlab-data", "groups"] });
        setNewChannelOpen(false);
        selectCollection(collection.id);
    };
    const activeEntry = useMemo(() => {
        if (selection?.kind === "group")
            return projects.find((p) => p.group.id === selection.id);
        if (selection?.kind === "collection")
            return projects.find((p) => p.channels.some((c) => c.id === selection.id));
        return undefined;
    }, [projects, selection]);
    const activeCollection = useMemo(() => {
        if (selection?.kind !== "collection")
            return undefined;
        return activeEntry?.channels.find((c) => c.id === selection.id);
    }, [activeEntry, selection]);
    const activeCollectionId = selection?.kind === "collection" ? selection.id : undefined;
    const detailsQ = useQuery({
        queryKey: ["vlab-data", "details", activeCollectionId],
        queryFn: () => getCollectionDetails(activeCollectionId, { limit: 60 }),
        enabled: activeCollectionId !== undefined,
    });
    // The collection to render. Prefer the tree row (carries blockCount /
    // lastAddedAt), but fall back to the `/details` payload so a collection
    // reached by direct id — and absent from the preloaded tree — still renders.
    const resolvedCollection = useMemo(() => activeCollection ?? detailsQ.data?.collection, [activeCollection, detailsQ.data]);
    // Breadcrumb group for the open collection. The tree entry wins; otherwise we
    // recover the parent group from the collection's `parentCollectionId`. A
    // standalone (parent-less) collection has no group → undefined (the
    // breadcrumb then drops the group segment).
    const resolvedGroup = useMemo(() => {
        if (activeEntry)
            return activeEntry.group;
        const pid = resolvedCollection?.parentCollectionId;
        if (pid === undefined)
            return undefined;
        return (projects.find((p) => p.group.id === pid)?.group ??
            groupsQ.data?.collections.find((g) => g.id === pid));
    }, [activeEntry, resolvedCollection, projects, groupsQ.data]);
    // Child stacks (layer 2) + their blocks — `/details` returns only the
    // collection's own top-level blocks, so stacked blocks are fetched here and
    // rendered as their own groups.
    const stacksQ = useQuery({
        queryKey: ["vlab-data", "stacks", activeCollectionId],
        enabled: activeCollectionId !== undefined,
        staleTime: LIST_STALE_MS,
        queryFn: async () => {
            const children = await listCollectionChildren(activeCollectionId);
            const stacks = children.filter((c) => (c.layerIndex ?? 2) === 2);
            return Promise.all(stacks.map(async (stack) => ({
                stack,
                blocks: (await getCollectionDetails(stack.id, { limit: 60 })).blocks,
            })));
        },
    });
    // Every block currently on screen (loose + stacked), for modal lookup and
    // resolving which collection an opened block belongs to.
    const allBlocks = useMemo(() => {
        const direct = detailsQ.data?.blocks ?? [];
        const stacked = stacksQ.data?.flatMap((s) => s.blocks) ?? [];
        return [...direct, ...stacked];
    }, [detailsQ.data, stacksQ.data]);
    // Block lookup for the query canvas overlay (every referenced id is a subset
    // of what we sent, which is drawn from `allBlocks`).
    const blocksById = useMemo(() => {
        const map = new Map();
        for (const e of allBlocks)
            map.set(e.block.id, e.block);
        return map;
    }, [allBlocks]);
    // Ask → temporary canvas. We hand the collection's blocks to the existing
    // collection-chat endpoint; it answers and names the relevant subset
    // (`referencedBlockIds`), which seeds a fresh board showing only those.
    const handleAsk = async (question) => {
        if (!resolvedCollection || asking)
            return;
        const ids = allBlocks.map((e) => e.block.id).slice(0, MAX_CANVAS_ASK_BLOCKS);
        if (ids.length === 0) {
            setAskError("No blocks to ask about yet.");
            return;
        }
        setAsking(true);
        setAskError(undefined);
        try {
            const title = collectionTitle(resolvedCollection);
            const res = await buildCollectionCanvas({ blockIds: ids, title, question });
            // Surfaced blocks, in the model's relevance order. Fall back to every
            // sent block if the model surfaced none, so the board isn't empty.
            const sent = new Set(ids);
            const surfaced = res.blocks.map((b) => b.blockId).filter((id) => sent.has(id));
            const canvas = createCanvas({
                question,
                answer: res.answer,
                collectionId: resolvedCollection.id,
                collectionTitle: title,
                blockIds: surfaced.length > 0 ? surfaced : ids,
                seedCaptions: res.blocks.map((b) => ({ blockId: b.blockId, text: b.caption })),
                seedGroups: res.groups,
            });
            setActiveCanvasId(canvas.id);
        }
        catch (e) {
            setAskError(e instanceof Error ? e.message : "Couldn't build a canvas.");
        }
        finally {
            setAsking(false);
        }
    };
    // Deep-link fallback: a shared `/<collection>/<blk_…>` URL (or a block paged
    // out of the first `getCollectionDetails` page, or in a not-yet-loaded stack)
    // points at a block that isn't in `allBlocks`. Fetch it directly by id so the
    // modal opens regardless — even before the collection grid finishes loading.
    const activeBlockInPage = useMemo(() => allBlocks.some((e) => e.block.id === activeBlockId), [allBlocks, activeBlockId]);
    const deepBlockQ = useQuery({
        queryKey: ["vlab-data", "block", activeBlockId],
        enabled: activeBlockId !== undefined && !activeBlockInPage,
        staleTime: LIST_STALE_MS,
        queryFn: () => fetchBlockEnvelope(activeBlockId),
    });
    const activeBlock = useMemo(() => {
        const inPage = allBlocks.find((e) => e.block.id === activeBlockId)?.block;
        if (inPage)
            return inPage;
        const fetched = deepBlockQ.data?.block;
        return fetched && fetched.id === activeBlockId ? fetched : undefined;
    }, [allBlocks, activeBlockId, deepBlockQ.data]);
    // Reading order for ←/→ navigation while a block is open: loose blocks first,
    // then each stack, mirroring how the grid/table/canvas lay them out.
    const orderedBlockIds = useMemo(() => allBlocks.map((e) => e.block.id), [allBlocks]);
    // The block locked for the inspect/ask gesture, resolved from the loaded set.
    const inspectBlock = useMemo(() => allBlocks.find((e) => e.block.id === inspectBlockId)?.block, [allBlocks, inspectBlockId]);
    // Debug: every *pure link* in the open collection (a block with a source URL
    // and no media of its own), offered to the screenshot scraper (see DebugDock).
    //
    // The media guard matters: `blockLinkUrl` also returns `metadata.source.url`
    // for non-link blocks, so without it an image captured from a webpage would
    // be scraped — and urlbox renders that URL onto a black 800×800 canvas, which
    // then flashed in over the image on hover. Image/video blocks carry their own
    // media and must never be screenshotted.
    const scrapeTargets = useMemo(() => {
        const out = [];
        for (const e of allBlocks) {
            if (blockMediaUrl(e.block) !== undefined)
                continue;
            const url = blockLinkUrl(e.block);
            if (url)
                out.push({ blockId: e.block.id, url });
        }
        return out;
    }, [allBlocks]);
    const blockCollectionName = useMemo(() => {
        const names = new Map();
        if (resolvedCollection)
            names.set(resolvedCollection.id, collectionTitle(resolvedCollection));
        for (const s of stacksQ.data ?? [])
            names.set(s.stack.id, collectionTitle(s.stack));
        return (id) => names.get(id) ?? (resolvedCollection ? collectionTitle(resolvedCollection) : "—");
    }, [resolvedCollection, stacksQ.data]);
    // Other blocks in this collection, offered as link-annotation targets.
    const linkCandidates = useMemo(() => allBlocks
        .filter((e) => e.block.id !== activeBlockId)
        .map((e) => ({ id: e.block.id, label: blockCaption(e.block) })), [allBlocks, activeBlockId]);
    // Keyboard control for the open block: Esc closes it; ←/→ step to the
    // previous/next block in the collection. Arrow keys are ignored while typing
    // in a field (so they still move the caret in the annotation composer), and
    // navigation simply stops at the first/last block rather than wrapping.
    useEffect(() => {
        if (!activeBlock)
            return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                closeBlock();
                return;
            }
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight")
                return;
            const target = e.target;
            const typing = target?.isContentEditable === true ||
                target?.matches?.("input, textarea, select, [contenteditable='true']") === true;
            if (typing)
                return;
            const idx = orderedBlockIds.indexOf(activeBlock.id);
            if (idx === -1)
                return;
            const nextIdx = idx + (e.key === "ArrowRight" ? 1 : -1);
            if (nextIdx < 0 || nextIdx >= orderedBlockIds.length)
                return;
            e.preventDefault();
            setActiveBlockId(orderedBlockIds[nextIdx]);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [activeBlock, orderedBlockIds, closeBlock]);
    // Track which block the pointer is over via the `data-vld-block-id` markers.
    // A ref (not state) so moving the mouse never re-renders the grid — the value
    // is only read on demand when Space is pressed.
    useEffect(() => {
        const onOver = (e) => {
            const el = e.target?.closest?.("[data-vld-block-id]");
            hoveredBlockIdRef.current = el?.getAttribute("data-vld-block-id") ?? undefined;
        };
        window.addEventListener("pointerover", onOver);
        return () => window.removeEventListener("pointerover", onOver);
    }, []);
    // Space locks the hovered block for Q&A; Esc clears the lock. Space is ignored
    // while typing in a field (so the search/ask inputs keep their spaces) and
    // while a block modal is open.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setInspectBlockId(undefined);
                return;
            }
            if (e.code !== "Space" && e.key !== " ")
                return;
            if (activeBlock)
                return;
            const target = e.target;
            const typing = target?.isContentEditable === true ||
                target?.matches?.("input, textarea, select, [contenteditable='true']") === true;
            if (typing)
                return;
            const hovered = hoveredBlockIdRef.current;
            if (hovered === undefined)
                return;
            e.preventDefault();
            setInspectBlockId(hovered);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [activeBlock]);
    // ⌘G (Ctrl-G elsewhere) opens the index/search modal with its field focused.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key.toLowerCase() !== "g")
                return;
            if ((e.metaKey || e.ctrlKey) === false)
                return;
            if (e.shiftKey || e.altKey)
                return;
            e.preventDefault();
            openSearch("collections");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    const listLoading = groupsQ.isLoading || collectionsQ.isLoading;
    const listError = groupsQ.isError || collectionsQ.isError;
    // Collection/group title by id, for the activity feed's "in …" context. A
    // miss (a block in a collection the tree doesn't surface) just drops the line.
    const collectionNameById = useMemo(() => {
        const map = new Map();
        for (const g of groupsQ.data?.collections ?? [])
            map.set(g.id, groupTitle(g));
        for (const c of collectionsQ.data?.collections ?? [])
            map.set(c.id, collectionTitle(c));
        return (id) => map.get(id) ?? "";
    }, [groupsQ.data, collectionsQ.data]);
    // The flat collection list feeding the index modal (A–Z + recency). Built from
    // the loaded layer-1 collections, with a per-row tint from each collection's
    // color so the index reads like the old swatch-coded sidebar.
    const indexCollections = useMemo(() => projects.flatMap((entry) => entry.channels.map((c) => ({
        id: c.id,
        title: collectionTitle(c),
        count: c.blockCount,
        recencyMs: recencyMs(c),
        color: swatchColor(c),
        projectId: entry.group.id,
    }))), [projects]);
    // Projects (groups) with at least one channel, for the index modal's
    // "look through" filter. Same recency order as the sidebar.
    const indexProjects = useMemo(() => projects
        .filter((entry) => entry.channels.length > 0)
        .map((entry) => ({ id: entry.group.id, title: groupTitle(entry.group) })), [projects]);
    let main;
    if (listLoading) {
        main = _jsx(Empty, { children: "Loading collections\u2026" });
    }
    else if (listError) {
        main = _jsx(Empty, { children: "Couldn't reach the collection API. Are you signed in?" });
    }
    else if (selection?.kind === "activity") {
        main = (_jsx(ActivityFeedView, { collectionName: collectionNameById, onOpenBlock: openBlockInCollection }));
    }
    else if (projects.length === 0) {
        main = _jsx(Empty, { children: "No groups or collections yet \u2014 create one in the app to see it here." });
    }
    else if (selection?.kind === "collection") {
        if (resolvedCollection) {
            main = (_jsx(CollectionView, { group: resolvedGroup, collection: resolvedCollection, blocks: detailsQ.data?.blocks, stacks: stacksQ.data, count: detailsQ.data?.count, isLoading: detailsQ.isLoading, isError: detailsQ.isError, stacksLoading: stacksQ.isLoading, asking: asking, askError: askError, onAsk: (q) => void handleAsk(q), onOpenGroup: selectGroup, onOpenBlock: openBlock, isPinned: pinnedIds.has(resolvedCollection.id), onTogglePin: togglePin }, resolvedCollection.id));
        }
        else if (detailsQ.isError) {
            main = (_jsx(Empty, { children: "Couldn't open that collection \u2014 it may have been deleted, or you may not have access." }));
        }
        else {
            main = _jsx(Empty, { children: "Loading collection\u2026" });
        }
    }
    else if (activeEntry) {
        main = _jsx(GroupView, { entry: activeEntry, onSelectCollection: selectCollection });
    }
    else {
        main = _jsx(Empty, { children: "Select a collection." });
    }
    return (_jsxs("div", { className: "vlab", children: [_jsxs("div", { className: "vlab-body", children: [_jsx(DataSidebar, { projects: projects, selection: selection, collapsed: sidebarCollapsed, onToggleCollapse: () => setSidebarCollapsed((v) => !v), onSelectCollection: selectCollection, onSelectActivity: selectActivity, onOpenSearch: () => openSearch("collections"), onNewChannel: () => setNewChannelOpen(true), pinnedIds: pinnedIds, onTogglePin: togglePin }), _jsx("main", { className: "vlab-main", children: main })] }), _jsx(SearchModal, { open: searchOpen, onClose: () => setSearchOpen(false), collections: indexCollections, projects: indexProjects, onOpenCollection: selectCollection, onOpenBlock: openBlockInCollection, initialScope: searchScope }), _jsx(NewChannelModal, { open: newChannelOpen, groups: newChannelGroups, defaultGroupId: newChannelDefaultGroupId, onClose: () => setNewChannelOpen(false), onCreated: handleChannelCreated }), activeBlock ? (_jsx(BlockAnnotateModal, { block: activeBlock, collectionName: blockCollectionName(activeBlock.collectionId), candidates: linkCandidates, onClose: closeBlock, onOpenLink: (id) => setActiveBlockId(id) }, activeBlock.id)) : undefined, inspectBlockId !== undefined && !activeBlock ? (_jsxs(_Fragment, { children: [_jsx(BlockInspectorOverlay, { blockId: inspectBlockId }), inspectBlock ? (_jsx(AskBar, { block: inspectBlock, onClose: () => setInspectBlockId(undefined) }, inspectBlockId)) : undefined] })) : undefined, activeCanvasId ? (_jsx(QueryCanvasOverlay, { canvasId: activeCanvasId, blocksById: blocksById, onClose: () => setActiveCanvasId(undefined) })) : undefined, _jsx(QuickCapture, { collectionId: activeCollectionId, onInsert: insertOptimisticBlock, onRemove: removeOptimisticBlock }), _jsx(DebugDock, { targets: scrapeTargets })] }));
};
export default HomeScreen;
